// src/modals/ResaV2Modal.jsx — Phase 2.G.1 (ADR-028) + PR #168 quick wins UX
// Modale Fiche Résa V2 (lecture seule) avec onglet Infos.
//
// Architecture multi-onglets prête pour Phases 2.G.2-5 :
//   - Infos (Phase 2.G.1) ACTIF
//   - Messages (Phase 2.G.4) stub disabled
//   - Paiements (Phase 2.G.2) stub disabled
//   - Contrat / Facture / Checklists / Formulaires — stubs Phase ult.
//
// Trigger : Phase 2.G.1 = bouton "Voir fiche" dans Beds24SyncPanel
//           Phase 2.G.3 = clic sur résa dans le calendrier mensuel

import { useState, useEffect, useMemo, useCallback } from "react"
import FeuilleConfirmation from '../components/FeuilleConfirmation'
import { Info, MessageSquare, CreditCard, Sparkles, FileSignature, Receipt, ListChecks, Files, User, CalendarDays, FileText, IdCard, CheckCircle, ClipboardList, Inbox, Loader2, XCircle, HelpCircle, AlertTriangle } from "lucide-react"
import { supabase } from "../supabase"
import { DS } from "../lib/ui"
import { formatDateFR, formatDateTimeFR, nightsBetween, fmtE } from "../lib/format"
import { findLogementInCfg, blockToGuestFields } from "../lib/sousloc-helpers"
import { listFormTokensByReservation, listFormSubmissions, listIdDocumentsByTokens, idDocDownloadUrl, idDocRecheck, contractDownloadUrl, edlAdminGet, rouvrirFormulaire } from "../lib/forms-api"  // Fiches d'arrivée LOT 3 (ADR-188) + pièces d'identité N2-IA LOT 2 (ADR-189) + contrat signé (micro-lot Documents) + état des lieux voyageur (EDL, lecture seule v1)
import { migrateLegacyToUnified } from "../lib/forms-migration"  // résolution des labels de champs (LOT 3)
import FormSubmissionView from "../components/FormSubmissionView"  // rendu durci payload (LOT 1 #522)
import { getResaOrBlock } from "../lib/resa-or-block-loader"  // PR #169a-bis (ADR-105)
import ThreadDisplay from "../tabs/messages/ThreadDisplay"
import { genFactureResaHTML } from "../lib/facture-resa-html"
import { ouvrirApercuIsole } from "../lib/apercu-isole"
import { resolveEmitter } from "../lib/emitter-resolver"  // fix facture-minitab-apercu-seul
import { docLabelForProp, resolveInvoicePrefix } from "../lib/invoices"  // fix facture-minitab-apercu-seul
import { computePreviewNumber } from "../lib/invoice-preview"  // fix facture-minitab-apercu-seul
import { genContratHTML } from "../lib/contrat-html"  // PR #169a v1.4 (ADR-104)
import { nomLogementFacture, RAISON_CONTRAT_SANS_NOM } from "../lib/nom-logement-facture"
import { resolveContratCautionDueClient } from "../lib/contrat-caution-apercu"  // CONTRAT V2 (revue M1)
import GuestFieldsForm from "./_GuestFieldsForm"  // PR #169a-bis — édition kind=pricing_block
import BlockEditDatesModal from "./BlockEditDatesModal"  // Lot 2 UI (ADR-258, #883) — édition dates blocage simple
import ResaMessageComposerWrapper from "./ResaMessageComposerWrapper"  // PR #170 (ADR-107 Option Y)
import ResaJourneyTimeline from "./ResaJourneyTimeline"  // Lot « conversation enrichie » — timeline envois auto
import PaiementsTabBody from "./_PaiementsTabBody"  // Phase 2 Stripe C8 (ADR-108)
import { fetchLogInfo } from "../lib/menage-log-info-cache"  // LOT Photos ménage 27
import { listIncidentsByRun, statusMeta, fetchIncidentPhotos } from "../lib/incidents-api"  // LOT Photos ménage 27 + Lot 2 photo signed (ADR-209)
import { groupPhotosByPiece, buildItemsCheckedDetail } from "../lib/menage-run-restitution"  // LOT Photos ménage 27
import ChecklistRunModal from "./ChecklistRunModal"  // ADR-208 — édition admin checklist (Lot 2 verrou 48h)
import { todayParisIso } from "../views/MenageTodayView"  // ADR-208 — date Paris (pure) pour masquer le bouton sur les runs futurs
import { useMenagePhotoUploadQueue } from "../lib/use-photo-upload-queue"  // ADR-208 — drain file photo en contexte admin
import { commitUploadedPhoto } from "../lib/menage-photo-commit"  // ADR-208
import GuestTimesPills from "../components/GuestTimesPills"  // Heures voyageur (formulaire de bienvenue) — ADMIN, ADR-226
import LiensVoyageurSection from "./LiensVoyageurSection"  // CHANTIER LIEN COURT — boutons « copier le lien » (espace séjour / livret / reçu)
import LivretOverrideSection from "./LivretOverrideSection"  // DÉROGATION MANUELLE — débloquer le livret (et le code d'accès) au cas par cas
import CopyGuestLinkButton from "../components/CopyGuestLinkButton"  // CHANTIER LIEN COURT — copie via lien court /l/<code> + repli lien long

// PR #168 — Onglets Ressources + Journal retirés (jamais livrés, polluent l'UX).
// Contrat reste mais devient conditionnel selon source : enabled si direct,
// disabled visible "Phase 2.G+" si airbnb/booking (cf. getEffectiveTabs ci-dessous).
const TABS = [
  { id: 'infos',       label: 'Infos',       icon: Info,          enabled: true,  phase: '2.G.1' },
  { id: 'messages',    label: 'Messages',    icon: MessageSquare, enabled: true,  phase: '2.G.4' },
  { id: 'paiements',   label: 'Paiements',   icon: CreditCard,    enabled: true,  phase: '2.G.2' },
  { id: 'menage',      label: 'Ménage',      icon: Sparkles,      enabled: true,  phase: '4 PR3' },
  { id: 'contrat',     label: 'Contrat',     icon: FileSignature, enabled: false, phase: '2.G+' },
  { id: 'facture',     label: 'Reçu voyageur', icon: Receipt,     enabled: true,  phase: '2.E.11' },
  { id: 'checklists',  label: 'Checklists',  icon: ListChecks,    enabled: false, phase: '2.G+' },
  { id: 'formulaires', label: "Documents",   icon: Files,         enabled: true,  phase: 'LOT 3' },
]

// PR #168 D-6/D-7 — Onglet Contrat enabled uniquement pour les résas directes.
// Pour Airbnb / Booking : Phase 2.G+ (contrats OTA gérés par la plateforme).
// Disabled visible cohérent avec le pattern existant (tooltip Phase ult.).
//
// PR #169a-bis Q-D8 (Option A) — signature étendue : accepte un `item`
// avec discriminator `kind` (cohérent ResaV2Modal v1.1).
// Contrat enabled si :
//   - kind='reservation' AND source='direct', OU
//   - kind='pricing_block' AND guest_first_name NOT NULL
// Rétrocompat : si item passé n'a pas de `kind`, on assume 'reservation'
// (callers historiques qui passent une row reservations brute).
export function getEffectiveTabs(item) {
  if (!item) {
    return TABS.map(t => (t.id === 'contrat' ? { ...t, enabled: false } : t))
  }
  // Rétrocompat : si on reçoit une row reservations brute (pas notre wrapper item),
  // on lit source directement.
  const kind = item.kind || 'reservation'
  let contratEnabled = false
  if (kind === 'reservation') {
    contratEnabled = item.source === 'direct'
  } else if (kind === 'pricing_block') {
    // Pour pricing_block, on lit guest_first_name sur raw OU shape (selon caller).
    const firstName =
      (item.raw && item.raw.guest_first_name) ||
      (item.shape && item.shape.guest_first_name) ||
      item.guest_first_name
    contratEnabled =
      typeof firstName === 'string' && firstName.trim() !== ''
  }
  return TABS.map(t => (t.id === 'contrat' ? { ...t, enabled: contratEnabled } : t))
}

const SOURCE_BG = { airbnb: '#FF5A5F', booking: '#003580', direct: DS.navy }
const STATUS_BG = { confirmed: DS.green, cancelled: DS.red, pending: DS.amber }

// PR #169a-bis (ADR-105) — couleur badge violet pastel pour pricing_block
// avec voyageur (résa direct interne FBZ). Cohérent ADR-104 Q-D15 + _block-visual.js.
const BLOCK_DIRECT_BG = '#a78bfa' // violet-400 lisible avec texte blanc
const BLOCK_EMPTY_BG = '#94a3b8'  // slate-400 (blocage simple)

// PR #169a-bis — placeholder onglets vides (state vide cohérent UX 8 onglets).
function EmptyTabPlaceholder({ icon: Icon, title, message }) {
  return (
    <div
      data-testid="empty-tab-placeholder"
      style={{
        padding: 48,
        textAlign: "center",
        color: DS.textMuted,
        fontSize: 13,
        lineHeight: 1.5,
      }}
    >
      <div style={{ width: 56, height: 56, borderRadius: 16, background: DS.cielSoft, color: DS.marine, display: "inline-flex", alignItems: "center", justifyContent: "center", marginBottom: 12 }}>{Icon && <Icon size={26} strokeWidth={1.8} />}</div>
      <div style={{ fontFamily: DS.fontDisplay, fontWeight: 600, color: DS.marineDark, marginBottom: 6 }}>{title}</div>
      {message && (
        <div style={{ fontSize: 12, color: DS.textMuted, maxWidth: 480, margin: '0 auto' }}>
          {message}
        </div>
      )}
    </div>
  )
}

/**
 * Modale unifiée résa + pricing_block.
 *
 * PR #169a-bis (ADR-105) — nouvelle signature props :
 *   @param {string} itemId — UUID résa OU pricing_block
 *   @param {'reservation'|'pricing_block'} itemKind — discriminator obligatoire
 *   @param {object} cfg — config FBZ (cfg.props[].logs[])
 *   @param {function} onClose
 *   @param {function} [onItemAction] — callback(type, result) propagé pour
 *     release/push/updated pricing_blocks → CalendarPanel invalide caches.
 *   @param {boolean} [dryRunActive] — affichage labels DRY_RUN (FooterActions block)
 *   @param {string} [reservationId] — DÉPRÉCIÉ, alias legacy pour itemId+kind='reservation'
 */
export default function ResaV2Modal({
  itemId,
  itemKind,
  cfg,
  onClose,
  onItemAction,
  dryRunActive,
  reservationId, // alias legacy
}) {
  // MPR169a-bis-3 — Rétrocompat alias : reservationId → itemId+kind='reservation'.
  // console.warn déprécié (cleanup planifié PR #169b/#170).
  const effectiveId = itemId || reservationId
  const effectiveKind = itemKind || (reservationId ? 'reservation' : null)
  useEffect(() => {
    if (reservationId && !itemId && typeof console !== 'undefined') {
      // eslint-disable-next-line no-console
      console.warn(
        "[ResaV2Modal] prop `reservationId` est déprécié — utiliser `itemId` + `itemKind`. Alias maintenu jusqu'à PR #169b/#170 cleanup.",
      )
    }
  }, [reservationId, itemId])

  // item : { kind, id, raw, shape } unifié (cf. getResaOrBlock).
  const [item, setItem] = useState(null)
  const [loading, setLoading] = useState(true)
  // Relecture d'un item DÉJÀ affiché (onUpdated des onglets) — cf. load().
  const [refreshing, setRefreshing] = useState(false)
  const [error, setError] = useState(null)
  const [activeTab, setActiveTab] = useState('infos')

  // Lookup logement depuis cfg.props[].logs[] (helper canonique findLogementInCfg
  // — couvre les chambres feuilles sous-loc p6 "Maison Zen" / lc1-lc3, bug D-1
  // PR #168). Inclut la référence prop complète pour l'architecture multi-émetteur
  // de FactureMiniTab (Bug #1 audit visuel global 06/05/2026).
  const logementInfo = useMemo(() => {
    if (!item || !cfg) return null
    const log = findLogementInCfg(cfg, item.shape?.logement_id)
    if (!log) return null
    // findLogementInCfg renvoie propId/propNom — on lookup la prop complète
    // pour la passer à FactureMiniTab (qui a besoin de prop.entiteId, etc.).
    const prop = (cfg.props || []).find(p => p.id === log.propId) || null
    return {
      id: log.id,
      nom: log.nom,
      nom_complet: log.nom_complet || log.nom,
      propNom: log.propNom,
      prop,
    }
  }, [item, cfg])

  // PR #168 D-6/D-7 + PR #169a-bis Q-D8 — onglets résolus selon kind+source/guest.
  // Contrat enabled si reservation+direct OU pricing_block+guest_first_name.
  const effectiveTabs = useMemo(() => getEffectiveTabs(item), [item])

  // NQAbis-3 fix — race condition cleanup. Navigation rapide calendrier
  // (clic résa → clic block → clic résa) pouvait afficher la data du PREMIER
  // load résolu après le second, car aucun cancel flag n'était posé.
  // Pattern emprunté à MessagesTab (cf. lignes plus bas) — `cancelled` flag
  // empêche setState après unmount/relance du useEffect.
  //
  // Note : `load` reste exposée hors useEffect pour le bouton "Réessayer"
  // (UI error state), donc on duplique la logique avec un guard local pour
  // l'effect tout en gardant la fonction load() utilisable par le bouton.
  async function load() {
    if (!effectiveId || !effectiveKind) {
      setError("Identifiant ou type manquant")
      setLoading(false)
      return
    }
    // RAFRAÎCHISSEMENT ≠ PREMIER CHARGEMENT (défaut trouvé le 2026-09-15 par
    // les tests e2e réveillés). `load()` est aussi l'`onUpdated` des onglets :
    // après une capture / libération / renvoi de caution, Paiements l'appelle
    // pour relire la résa. Passer `loading` à true DÉMONTAIT tout le corps
    // (« Chargement… ») et jetait l'état local de l'onglet — dont le message
    // « Capture validée — X € débités ». Après un débit RÉEL, l'admin ne
    // voyait jamais la confirmation : la modale clignotait et revenait avec le
    // statut à jour, muette. On ne démonte donc plus quand un item est déjà
    // affiché : la relecture met à jour `item` en place, l'onglet garde son
    // message. Le premier chargement (pas d'item) garde son « Chargement… ».
    if (!item) setLoading(true)
    else setRefreshing(true)
    setError(null)
    try {
      const fetched = await getResaOrBlock(effectiveId, effectiveKind)
      setItem(fetched)
    } catch (e) {
      setError(e.message || String(e))
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      if (!effectiveId || !effectiveKind) {
        if (!cancelled) {
          setError("Identifiant ou type manquant")
          setLoading(false)
        }
        return
      }
      if (!cancelled) {
        setLoading(true); setError(null)
      }
      try {
        const fetched = await getResaOrBlock(effectiveId, effectiveKind)
        if (cancelled) return
        setItem(fetched)
      } catch (e) {
        if (cancelled) return
        setError(e.message || String(e))
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [effectiveId, effectiveKind])

  // Fermeture sur Escape
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  // Helper UX : propage action post-save vers CalendarPanel (invalidate caches).
  const handleItemAction = (type, result) => {
    if (typeof onItemAction === 'function') {
      try { onItemAction(type, result) } catch (_) { /* silent */ }
    }
  }

  if (!effectiveId) return null

  return (
    <div onClick={onClose}
      style={{position:"fixed",inset:0,background:"rgba(0,0,0,.5)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center",padding:16,fontFamily:DS.fontBody}}>
      <div onClick={e => e.stopPropagation()} className="fbz-modal-sheet"
        style={{background:DS.white,borderRadius:12,maxWidth:900,width:"100%",maxHeight:"90vh",overflow:"hidden",display:"flex",flexDirection:"column"}}>

        {loading && <div style={{padding:48,textAlign:"center",color:"#888",fontSize:13}}>Chargement…</div>}
        {/* Erreur SANS fiche affichée : écran d'erreur + Réessayer (premier chargement). */}
        {error && !item && (
          <div style={{padding:48,textAlign:"center"}}>
            <div style={{color:DS.red,fontSize:13,marginBottom:12}}>{error}</div>
            <button onClick={load} style={{background:DS.navy,color:"#fff",border:"none",borderRadius:8,padding:"8px 18px",fontSize:12,fontWeight:700,cursor:"pointer"}}>Réessayer</button>
          </div>
        )}
        {/* Erreur ou relecture AVEC une fiche déjà affichée : BANDEAU, jamais à la
            place du corps (revue adverse 2026-09-15). Démonter le corps ici
            jetait l'état des onglets — dont « Capture validée — X € débités » —
            et l'admin lisait « l'erreur, c'est que la capture a échoué » alors
            que l'argent était parti. Le corps reste ; il relit un item qui peut
            être PÉRIMÉ le temps de la relecture, et le bandeau le dit. */}
        {item && refreshing && !error && (
          <div data-testid="resa-modal-refreshing" style={{padding:"6px 24px",background:DS.card,color:"#888",fontSize:11}}>Relecture de la réservation en cours…</div>
        )}
        {item && error && (
          <div data-testid="resa-modal-refresh-error" style={{padding:"8px 24px",background:DS.dangerBg,color:DS.red,fontSize:12,display:"flex",gap:12,alignItems:"center"}}>
            <span style={{flex:1}}>Relecture impossible : {error} — les informations affichées peuvent être périmées ; l'action précédente, elle, a bien été enregistrée si son message l'indique.</span>
            <button onClick={load} style={{background:DS.navy,color:"#fff",border:"none",borderRadius:8,padding:"6px 14px",fontSize:11,fontWeight:700,cursor:"pointer"}}>Relire</button>
          </div>
        )}

        {!loading && item && (
          <>
            <Header item={item} logementInfo={logementInfo} onClose={onClose} />
            <TabsBar tabs={effectiveTabs} active={activeTab} onChange={setActiveTab} />
            <div style={{flex:1,overflowY:"auto",padding:24,display:"flex",flexDirection:"column"}}>
              {activeTab === 'infos'     && (
                <InfosTab
                  item={item}
                  logementInfo={logementInfo}
                  onUpdated={() => {
                    load()
                    handleItemAction('updated', { kind: item.kind, id: item.id })
                  }}
                />
              )}
              {activeTab === 'paiements' && (
                // Phase 2 Stripe C8 (ADR-108) — PaiementsTab kind-aware.
                // pricing_block (résa interne FBZ Direct) = supporté comme
                // 'direct' (mêmes colonnes Stripe via migration 20260526000001
                // + propagation mapBlockToReservationShape).
                <PaiementsTab
                  r={item.shape}
                  itemKind={item.kind}
                  cfg={cfg}
                  onUpdated={load}
                  // Lot annulation résa payée (2e accès calendrier) — après
                  // annulation réussie : invalidation caches calendrier
                  // (pattern 'updated' existant) + fermeture propre de la
                  // fiche (même geste que 'released' FooterActions).
                  onBookingCancelled={() => {
                    handleItemAction('updated', { kind: item.kind, id: item.id })
                    onClose()
                  }}
                />
              )}
              {activeTab === 'messages'  && (
                // PR #170 (ADR-107 Option Y) — read+write actif pour
                // reservation ET pricing_block. Le wrapper gère l'empty
                // state (aucun canal) côté composer si guest_email absent.
                <MessagesTab item={item} />
              )}
              {activeTab === 'menage'    && (
                item.kind === 'pricing_block'
                  ? <EmptyTabPlaceholder
                      icon={Sparkles}
                      title="Pas de cycle ménage géré"
                      message="Logique cron Beds24 ne touche pas les résas internes. Cycle manuel possible via Phase 4 PR ult."
                    />
                  : <MenageTab r={item.shape} logementInfo={logementInfo} />
              )}
              {activeTab === 'facture'   && (
                <FactureMiniTab
                  r={item.shape}
                  itemKind={item.kind}
                  logementInfo={logementInfo}
                  cfg={cfg}
                />
              )}
              {activeTab === 'contrat'   && (
                <ContratTab
                  r={item.shape}
                  itemKind={item.kind}
                  logementInfo={logementInfo}
                  cfg={cfg}
                />
              )}
              {activeTab === 'checklists' && (
                <EmptyTabPlaceholder
                  icon={CheckCircle}
                  title="Checklists à configurer"
                  message="Phase 2.G+"
                />
              )}
              {activeTab === 'formulaires' && (
                <>
                  {/* CHANTIER LIEN COURT — liens à envoyer À LA MAIN au voyageur
                      (espace séjour / livret / reçu). Le lien de PAIEMENT reste
                      dans l'onglet Paiements, jamais raccourci. */}
                  <LiensVoyageurSection item={item} />
                  {/* DÉROGATION MANUELLE — placée ENTRE le lien du livret
                      (au-dessus) et l'état des fiches d'arrivée (en dessous) :
                      le propriétaire voit la cause (« formulaire non rempli »)
                      et le remède au même endroit, sur LA réservation qu'il
                      traite — jamais dans un réglage global. Le geste ouvre
                      AUSSI le code d'accès : l'écran l'annonce avant de poser. */}
                  <LivretOverrideSection item={item} />
                  <FichesArriveeTab item={item} cfg={cfg} />
                </>
              )}
            </div>
            {/* FooterActions pour kind=pricing_block (release/push) — PR #169a-bis C3. */}
            {item.kind === 'pricing_block' && (
              <FooterActions
                item={item}
                dryRunActive={dryRunActive}
                onAction={(type, result) => {
                  handleItemAction(type, result)
                  if (type === 'released') {
                    onClose()
                  } else if (type === 'pushed') {
                    load() // recharge état beds24_pushed
                  }
                }}
              />
            )}
          </>
        )}
      </div>
    </div>
  )
}

// ── Header (sticky) ─────────────────────────────────────────────────

// Export nommé pour tests (composant pur, pattern GuestTimesLine/MenageTodayView).
export function Header({ item, logementInfo, onClose }) {
  // PR #169a-bis (ADR-105) — Header kind-aware.
  const r = item.shape
  const isBlock = item.kind === 'pricing_block'
  // LPR169a-bis-3 — fallback prénom pour blocage simple (sans voyageur).
  const hasGuestName =
    (r.guest_first_name && r.guest_first_name.trim() !== '') ||
    (r.guest_last_name && r.guest_last_name.trim() !== '')
  let fullName
  if (hasGuestName) {
    fullName = `${r.guest_first_name || ''} ${r.guest_last_name || ''}`.trim()
  } else if (isBlock) {
    fullName = '(Blocage simple)'
  } else {
    fullName = '?'
  }
  const nights = nightsBetween(r.start_date, r.end_date)

  // Badge source : pour pricing_block avec voyageur, on affiche "direct" (Q-D10).
  // Pour pricing_block sans voyageur (blocage simple) : "blocked" gris.
  let sourceLabel, sourceColor
  if (isBlock) {
    if (hasGuestName) {
      sourceLabel = 'direct'
      sourceColor = BLOCK_DIRECT_BG // violet cohérent _block-visual.js
    } else {
      sourceLabel = 'blocked'
      sourceColor = BLOCK_EMPTY_BG
    }
  } else {
    sourceLabel = r.source
    sourceColor = SOURCE_BG[r.source] || DS.navy
  }

  const statusColor = STATUS_BG[r.status] || DS.amber
  return (
    // PR #168 D-2 — flexShrink:0 obligatoire : sans cela, l'onglet Messages
    // (ThreadDisplay flex:1 minHeight:280 + body MessagesTab flex:1 minHeight:300)
    // exige ≥580px de hauteur et compresse Header + TabsBar à hauteur 0
    // (parent modal display:flex column maxHeight:90vh overflow:hidden).
    <div style={{padding:"20px 24px",borderBottom:"1px solid #e8edf4",background:DS.white,flexShrink:0}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",gap:12,flexWrap:"wrap"}}>
        <span style={{fontFamily:DS.fontDisplay,fontSize:18,fontWeight:700,color:DS.navy}}>{fullName}</span>
        <div style={{display:"flex",alignItems:"center",gap:6,flexWrap:"wrap"}}>
          {sourceLabel && (
            <span style={{padding:"3px 10px",borderRadius:14,background:sourceColor,color:"#fff",fontSize:10,fontWeight:700,letterSpacing:.3,textTransform:"uppercase"}}>
              {sourceLabel}
            </span>
          )}
          {r.status && (
            <span style={{padding:"3px 10px",borderRadius:14,background:statusColor,color:"#fff",fontSize:10,fontWeight:700,letterSpacing:.3,textTransform:"uppercase"}}>
              {r.status}
            </span>
          )}
          <button onClick={onClose} aria-label="Fermer"
            style={{background:"transparent",border:"none",fontSize:24,cursor:"pointer",color:"#888",padding:4,marginLeft:4,lineHeight:1}}>
            ×
          </button>
        </div>
      </div>
      <div style={{color:"#888",fontSize:13,marginTop:4}}>
        {formatDateFR(r.start_date)} → {formatDateFR(r.end_date)} ({nights} nuit{nights > 1 ? 's' : ''})
      </div>
      {/* Heures voyageur (formulaire de bienvenue, dernière soumission) —
          reservations.guest_arrival_time/guest_departure_time (chargées via
          getResaOrBlock select('*')). ADMIN uniquement (ADR-226 : cette modale
          n'est jamais montée côté proprio ni agent ménage). Rien rendu si
          aucune heure — le composant gère. Pour un pricing_block les colonnes
          n'existent pas → undefined → rien. */}
      <GuestTimesPills
        arrival={r.guest_arrival_time}
        departure={r.guest_departure_time}
        style={{ marginTop: 7 }}
      />
      <div style={{color:"#888",fontSize:12,marginTop:2}}>
        {logementInfo ? logementInfo.nom : <em style={{color:DS.red}}>(logement {r.logement_id} non trouvé dans cfg)</em>}
        {r.guest_count != null && <> · {r.guest_count} {r.guest_count > 1 ? 'voyageurs' : 'voyageur'}</>}
        {/* NQAbis-2 fix — raw.reason (pas raw._reason : `_reason` n'existe
            que sur item.shape via mapper, donc le short-circuit cachait
            toujours la raison). */}
        {isBlock && item.raw.reason && <> · <em style={{ color: "#888" }}>{item.raw.reason}</em></>}
      </div>
    </div>
  )
}

// ── Tabs Bar (sticky sous header) ──────────────────────────────────

function TabsBar({ tabs, active, onChange }) {
  return (
    // PR #168 D-2 — flexShrink:0 sur TabsBar (même raison que Header) :
    // évite la compression à 0 quand le contenu (Messages) demande beaucoup d'espace.
    <div data-testid="resa-modal-tabs" style={{padding:"0 24px",display:"flex",gap:2,borderBottom:`1px solid ${DS.border}`,background:DS.card,overflowX:"auto",flexShrink:0}}>
      {tabs.map(t => {
        const isActive = t.id === active
        const TabIco = t.icon
        // PR #168 D-7 — tooltip contextuel pour Contrat disabled :
        // précise que c'est lié à la source (gestion Airbnb/Booking côté plateforme).
        const tooltipDisabled = t.id === 'contrat'
          ? 'Phase 2.G+ pour résas Airbnb/Booking (contrats gérés par la plateforme)'
          : `Phase ${t.phase} à venir`
        return (
          <button key={t.id}
            data-testid={`resa-modal-tab-${t.id}`}
            onClick={() => t.enabled && onChange(t.id)}
            disabled={!t.enabled}
            title={!t.enabled ? tooltipDisabled : undefined}
            aria-disabled={!t.enabled || undefined}
            style={{
              display:"inline-flex",alignItems:"center",gap:6,
              padding:"10px 14px",border:"none",
              borderBottom:`2px solid ${isActive ? DS.corail : "transparent"}`,
              marginBottom:-1,background:"transparent",
              color: isActive ? DS.corail : (t.enabled ? DS.textSecondary : DS.textMuted),
              fontWeight: isActive ? 700 : 600,
              fontSize: 12, cursor: t.enabled ? "pointer" : "not-allowed",
              opacity: t.enabled ? 1 : 0.5,
              fontFamily: DS.fontBody, whiteSpace: "nowrap",
            }}>
            {TabIco && <TabIco size={14} strokeWidth={2.2} />}{t.label}
          </button>
        )
      })}
    </div>
  )
}

// ── Onglet Infos (lecture seule, 2 colonnes) ───────────────────────

function InfosTab({ item, logementInfo, onUpdated }) {
  // PR #169a-bis (ADR-105) — InfosTab kind-aware.
  // - kind='reservation' : édition Scope B 4 champs (airbnb/booking)
  // - kind='pricing_block' : édition voyageur 13 champs + total_amount via GuestFieldsForm
  const r = item.shape
  const isBlock = item.kind === 'pricing_block'
  const fullName = `${r.guest_first_name || ''} ${r.guest_last_name || ''}`.trim()
  const nights = nightsBetween(r.start_date, r.end_date)
  // Q-D9 — Toggle édition (Option A) : pricing_block toujours editable.
  // reservation : édition Scope B 4 champs si source=airbnb/booking.
  // reservation source=direct : pas d'édition (ADR-104, jamais editable).
  const canEditInternal =
    isBlock ||
    (item.kind === 'reservation' && (r.source === 'airbnb' || r.source === 'booking'))
  const [editing, setEditing] = useState(false)
  const [editFields, setEditFields] = useState({})
  const [submitting, setSubmitting] = useState(false)
  const [editError, setEditError] = useState(null)

  // Lot 2 UI (ADR-258, #883) — édition des DATES réservée aux blocages SIMPLES
  // (sans voyageur ni argent : mêmes conditions fail-closed que le serveur, qui
  // refuse sinon `direct_block_not_editable`). Le serveur reste juge in fine.
  const raw = item.raw || {}
  const blockHasGuest =
    (typeof raw.guest_first_name === 'string' && raw.guest_first_name.trim() !== '') ||
    (typeof raw.guest_last_name === 'string' && raw.guest_last_name.trim() !== '')
  const blockHasMoney =
    raw.total_amount != null ||
    raw.paiement_sejour_status != null ||
    raw.paiement_sejour_intent_id != null ||
    raw.caution_status != null ||
    raw.caution_payment_intent_id != null ||
    raw.contract_signed_at != null
  const canEditBlockDates = isBlock && !blockHasGuest && !blockHasMoney
  const [showDatesModal, setShowDatesModal] = useState(false)

  function startEdit() {
    if (isBlock) {
      // kind=pricing_block : pré-fill 13 champs voyageur + total_amount.
      setEditFields(blockToGuestFields(item.raw))
    } else {
      // kind=reservation : 4 champs Scope B (PR #169a v1.4).
      setEditFields({
        guest_address: r.guest_address || "",
        guest_city: r.guest_city || "",
        guest_postal_code: r.guest_postal_code || "",
        children_count: r.children_count != null ? String(r.children_count) : "",
      })
    }
    setEditError(null)
    setEditing(true)
  }

  function cancelEdit() {
    setEditing(false)
    setEditError(null)
  }

  async function saveEdit() {
    setSubmitting(true)
    setEditError(null)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session?.access_token) throw new Error("Non authentifié")
      // Normalisation : "" → null. children_count → integer (LPR169a-bis-1).
      const cleaned = {}
      for (const [key, val] of Object.entries(editFields)) {
        if (val === "" || val == null) {
          cleaned[key] = null
        } else if (key === "children_count" || key === "guest_count") {
          // LPR169a-bis-1 — coerce Number côté form.
          const n = Number(val)
          cleaned[key] = Number.isFinite(n) ? n : val
        } else {
          cleaned[key] = val
        }
      }

      // PR #169a-bis Q-D9 (Option A) — dispatcher endpoint selon kind+source.
      let url, body
      if (isBlock) {
        url = "/api/beds24-mapping?action=pricing_block_update_internal"
        body = { block_id: item.id, guest_fields: cleaned }
      } else if (item.kind === 'reservation' && r.source !== 'direct') {
        url = "/api/beds24-mapping?action=reservation_update_internal"
        body = { reservation_id: item.id, fields: cleaned }
      } else {
        // Defense-in-depth — UI cache déjà le bouton mais on throw si appelé.
        throw new Error("Édition non autorisée pour ce kind/source")
      }
      const res = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify(body),
      })
      const json = await res.json().catch(() => ({}))
      if (!res.ok) {
        throw new Error(json.message || json.error || `http_${res.status}`)
      }
      setEditing(false)
      onUpdated && onUpdated()
    } catch (e) {
      setEditError(e.message || String(e))
    } finally {
      setSubmitting(false)
    }
  }

  // Wrapper render pour pricing_block en édition : GuestFieldsForm + actions.
  if (isBlock && editing) {
    return (
      <div data-testid="infos-tab-block-edit-body">
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:12,flexWrap:"wrap",gap:8}}>
          <SectionTitle icon={User}>Détails voyageur (édition)</SectionTitle>
        </div>
        <GuestFieldsForm
          values={editFields}
          onChange={(key, val) => setEditFields((prev) => ({ ...prev, [key]: val }))}
          disabled={submitting}
        />
        {editError && (
          <div style={{marginTop:10,padding:"8px 10px",background:"#fdf2f2",color:DS.red,borderRadius:6,fontSize:11}}>
            {editError}
          </div>
        )}
        <div style={{marginTop:14,display:"flex",gap:8}}>
          {/* NQAbis-5 fix — testid spécifique au body pricing_block édition
              pour éviter collision avec celui du bloc reservation Scope B
              (ligne plus bas). Aide à cibler unitairement les E2E. */}
          <button
            data-testid="infos-tab-block-edit-save-button"
            onClick={saveEdit}
            disabled={submitting}
            style={{background:DS.navy,color:"#fff",border:"none",borderRadius:7,padding:"8px 14px",fontSize:12,fontWeight:700,cursor:submitting?"not-allowed":"pointer",opacity:submitting?0.5:1,fontFamily:DS.fontBody}}
          >
            {submitting ? "Envoi…" : "Enregistrer"}
          </button>
          <button
            onClick={cancelEdit}
            disabled={submitting}
            style={{background:"#eef2f7",color:"#666",border:"none",borderRadius:7,padding:"8px 14px",fontSize:12,fontWeight:600,cursor:submitting?"not-allowed":"pointer",fontFamily:DS.fontBody}}
          >
            Annuler
          </button>
        </div>
      </div>
    )
  }

  return (
    <>
    <div className="fbz-grid-collapse" style={{gap:32}}>
      {/* Colonne gauche — Détails voyageur */}
      <div>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:12,flexWrap:"wrap",gap:8}}>
          <SectionTitle icon={User}>Détails voyageur</SectionTitle>
          {/* PR #169a v1.4 Scope B — bouton Modifier visible uniquement
              pour airbnb/booking (édition limitée 4 champs internes). */}
          {canEditInternal && !editing && (
            <button
              data-testid="infos-tab-edit-internal-button"
              onClick={startEdit}
              style={{background:"#f1f5f9",color:DS.navy,border:"1px solid #dde3ec",borderRadius:6,padding:"5px 10px",fontSize:11,fontWeight:600,cursor:"pointer",fontFamily:DS.fontBody}}
            >
              Modifier
            </button>
          )}
        </div>
        <DataRow label="Prénom"     value={r.guest_first_name} />
        <DataRow label="Nom"        value={r.guest_last_name} />
        <DataRow label="Email"      value={r.guest_email}  isLink="mailto" />
        <DataRow label="Téléphone"  value={r.guest_phone}  isLink="tel" />
        <DataRow label="Pays"       value={r.guest_country} />
        {/* PR #169a v1.4 Scope B — 4 champs éditables en mode editing. */}
        {editing ? (
          <>
            <EditableRow
              label="Code postal"
              testid="infos-tab-edit-postal-code"
              value={editFields.guest_postal_code}
              onChange={(v) => setEditFields((p) => ({ ...p, guest_postal_code: v }))}
              disabled={submitting}
            />
            <EditableRow
              label="Adresse"
              testid="infos-tab-edit-address"
              value={editFields.guest_address}
              onChange={(v) => setEditFields((p) => ({ ...p, guest_address: v }))}
              disabled={submitting}
            />
            <EditableRow
              label="Ville"
              testid="infos-tab-edit-city"
              value={editFields.guest_city}
              onChange={(v) => setEditFields((p) => ({ ...p, guest_city: v }))}
              disabled={submitting}
            />
            <DataRow label="Adultes" value={r.guest_count != null ? String(r.guest_count) : null} />
            <EditableRow
              label="Enfants"
              testid="infos-tab-edit-children-count"
              value={editFields.children_count}
              onChange={(v) => setEditFields((p) => ({ ...p, children_count: v }))}
              disabled={submitting}
              type="number"
            />
          </>
        ) : (
          <>
            <DataRow label="Code postal" value={r.guest_postal_code} />
            <DataRow label="Adresse"    value={r.guest_address} />
            <DataRow label="Ville"      value={r.guest_city} />
            <DataRow label="Adultes"    value={r.guest_count != null ? String(r.guest_count) : null} />
            {(r.children_count != null && r.children_count > 0) && (
              <DataRow label="Enfants" value={String(r.children_count)} />
            )}
          </>
        )}
        <DataRow label="Langue"     value={r.guest_language} />
        {editing && (
          <div style={{marginTop:12,display:"flex",gap:8}}>
            <button
              data-testid="infos-tab-edit-save-button"
              onClick={saveEdit}
              disabled={submitting}
              style={{background:DS.navy,color:"#fff",border:"none",borderRadius:7,padding:"8px 14px",fontSize:12,fontWeight:700,cursor:submitting?"not-allowed":"pointer",opacity:submitting?0.5:1,fontFamily:DS.fontBody}}
            >
              {submitting ? "Envoi…" : "Enregistrer"}
            </button>
            <button
              onClick={cancelEdit}
              disabled={submitting}
              style={{background:"#eef2f7",color:"#666",border:"none",borderRadius:7,padding:"8px 14px",fontSize:12,fontWeight:600,cursor:submitting?"not-allowed":"pointer",fontFamily:DS.fontBody}}
            >
              Annuler
            </button>
          </div>
        )}
        {editError && (
          <div style={{marginTop:10,padding:"8px 10px",background:"#fdf2f2",color:DS.red,borderRadius:6,fontSize:11}}>
            {editError}
          </div>
        )}
        {canEditInternal && !editing && !isBlock && (
          <div style={{marginTop:14,fontSize:10,color:"#aaa",fontStyle:"italic",lineHeight:1.5}}>
            Édition limitée aux champs adresse + enfants (Scope B). Email,
            téléphone, notes restent en lecture seule pour éviter conflit cron
            Beds24 sync 6h.
          </div>
        )}
        {isBlock && !editing && (
          <div style={{marginTop:14,fontSize:10,color:"#aaa",fontStyle:"italic",lineHeight:1.5}}>
            Période bloquée / résa direct interne FBZ. Édition complète des
            13 champs voyageur via "Modifier" (cf. ADR-104 + ADR-105).
          </div>
        )}
      </div>

      {/* Colonne droite — Détails réservation / Période bloquée */}
      <div>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:12,flexWrap:"wrap",gap:8}}>
          <SectionTitle icon={CalendarDays}>{isBlock ? 'Détails période' : 'Détails réservation'}</SectionTitle>
          {/* Lot 2 UI (ADR-258, #883) — édition des dates d'un blocage simple.
              Le clic ouvre une modale dédiée (récap nuits rouvertes/fermées +
              confirmation 2 étapes pour la réouverture, chemin argent). */}
          {canEditBlockDates && !editing && (
            <button
              type="button"
              data-testid="block-edit-dates-open-button"
              onClick={() => setShowDatesModal(true)}
              style={{background:"#f1f5f9",color:DS.navy,border:"1px solid #dde3ec",borderRadius:6,padding:"5px 10px",fontSize:11,fontWeight:600,cursor:"pointer",fontFamily:DS.fontBody}}
            >
              Modifier les dates du blocage
            </button>
          )}
        </div>
        <DataRow label="Logement"     value={logementInfo ? (logementInfo.nom_complet || logementInfo.nom) : `${r.logement_id} (non trouvé)`} />
        <DataRow label="Source"       value={isBlock ? 'direct (interne FBZ)' : r.source} />
        <DataRow label="Statut"       value={r.status} />
        {/* Date résa OTA : masquer pour pricing_block (pas de booking_date résa OTA) */}
        {!isBlock && (
          <DataRow label="Date résa"    value={r.booking_date ? formatDateFR(r.booking_date) : (r.created_at ? formatDateFR(r.created_at) : null)} />
        )}
        <DataRow label="Check-in"     value={`${formatDateFR(r.start_date)}${r.arrival_time ? ' à ' + String(r.arrival_time).slice(0, 5) : ''}`} />
        <DataRow label="Check-out"    value={`${formatDateFR(r.end_date)}${r.departure_time ? ' à ' + String(r.departure_time).slice(0, 5) : ''}`} />
        <DataRow label="Durée"        value={`${nights} nuit${nights > 1 ? 's' : ''}`} />
        <DataRow label="Prix total"   value={r.total_amount != null ? fmtE(r.total_amount) : null} />
        {/* Q-D13 — bandeau warning total_amount pour pricing_block si > 0 */}
        {/* PR #169b (ADR-106) — bandeau "Comptabilisation en cours" retiré :
            agrégation effective via calcDirectInternePure + wrapper App.jsx. */}
        {/* Frais OTA : masquer pour pricing_block (résa interne pas d'OTA) */}
        {!isBlock && (r.cleaning_fee != null && Number(r.cleaning_fee) > 0) && (
          <DataRow label="Frais ménage" value={fmtE(r.cleaning_fee)} />
        )}
        {!isBlock && (r.city_tax != null && Number(r.city_tax) > 0) && (
          <DataRow label="Taxe séjour" value={fmtE(r.city_tax)} />
        )}
        {!isBlock && (r.ota_commission != null && Number(r.ota_commission) > 0) && (
          <DataRow label="Commission OTA" value={fmtE(r.ota_commission)} />
        )}
        {/* Raison block (uniquement pricing_block) */}
        {isBlock && (item.raw.reason || item.raw._reason) && (
          <DataRow label="Raison" value={item.raw.reason || item.raw._reason} />
        )}
        {/* Statut push Beds24 (pricing_block sans guest = blocage simple) */}
        {isBlock && !hasGuestFirstName(item) && (
          <DataRow
            label="Push Beds24"
            value={item.raw.beds24_pushed ? 'Poussé' : 'En attente'}
          />
        )}
        {/* Statut release (pricing_block) */}
        {isBlock && item.raw.released_at && (
          <DataRow
            label={item.raw.active === true ? "Libération demandée le" : "Libéré le"}
            value={formatDateFR(item.raw.released_at)}
          />
        )}
        {r.notes !== undefined && (
          <div style={{padding:"6px 0",borderBottom:"1px solid #f0f4f8"}}>
            <div style={{fontSize:11,color:"#888",fontWeight:500,marginBottom:4}}>Notes</div>
            <textarea readOnly value={r.notes || ''} rows={2}
              placeholder="—"
              style={{width:"100%",border:"1px solid #e8edf4",borderRadius:6,padding:"6px 8px",fontSize:12,background:"#f8fafd",resize:"vertical",fontFamily:DS.fontBody}}/>
          </div>
        )}
        <div style={{marginTop:12,fontSize:10,color:"#aaa",fontStyle:"italic"}}>
          UUID {isBlock ? 'block' : 'résa'} : <code style={{background:"#eef4fb",padding:"1px 5px",borderRadius:3}}>{r.id}</code>
          {!isBlock && r.beds24_booking_id && <> · Beds24 booking <code style={{background:"#eef4fb",padding:"1px 5px",borderRadius:3}}>{r.beds24_booking_id}</code></>}
        </div>
        <div style={{marginTop:4,fontSize:10,color:"#aaa",fontStyle:"italic"}}>
          Voyageur : {fullName || '(?)'}
        </div>
      </div>
    </div>
    {/* Lot 2 UI (ADR-258, #883) — modale d'édition des dates du blocage. Le
        succès recharge la fiche + invalide le calendrier via onUpdated. */}
    {showDatesModal && (
      <BlockEditDatesModal
        open
        blockId={item.id}
        logementLabel={logementInfo ? (logementInfo.nom_complet || logementInfo.nom) : r.logement_id}
        expectedStart={r.start_date}
        expectedEnd={r.end_date}
        onClose={() => setShowDatesModal(false)}
        onSaved={() => { setShowDatesModal(false); onUpdated && onUpdated() }}
      />
    )}
    </>
  )
}

// Helper local : pricing_block avec voyageur (résa direct interne).
function hasGuestFirstName(item) {
  if (!item || item.kind !== 'pricing_block') return false
  const name = item.raw?.guest_first_name
  return typeof name === 'string' && name.trim() !== ''
}

// ── Onglet Paiements (Phase 2.G.2 + Phase 2 Stripe C8 ADR-108) ──────
//
// Refonte Phase 2 Stripe C8 v1.3 — Stripe-centric UI :
//   - Airbnb : section read-only AirCover (inchangé PR #169a v1.4)
//   - Booking : Caution Stripe interactive (paiement séjour via OTA)
//   - Direct (reservation OR pricing_block) : Caution + Paiement Séjour
//
// Pattern ADR-099 (Option Hybride) : thin wrapper exporté ici, body extrait
// dans `_PaiementsTabBody.jsx` (>200 LOC). Rétrocompat : `PaiementsTab(r,
// onUpdated)` reste appelable (callers existants), mais préférable de passer
// `itemKind` + `cfg` pour activer la résolution stripe_account_id côté UI.
//
// Anciens toggles `payment_received_at` / `caution_received_at` retirés :
// le webhook Stripe (api/stripe-webhook.js) propage `caution_received_at` au
// statut Stripe (cf. ADR-027 welcome_checkin_v2 — gating inchangé côté
// rules-engine, source vérité = Stripe).

export function PaiementsTab({ r, itemKind, cfg, onUpdated, onBookingCancelled }) {
  return (
    <PaiementsTabBody
      r={r}
      itemKind={itemKind || "reservation"}
      cfg={cfg || null}
      onUpdated={onUpdated}
      onBookingCancelled={onBookingCancelled}
    />
  )
}

// ── Onglet Messages (Phase 2.G.4 + PR #170) ─────────────────────────
// Lecture + écriture des messages inbox associés à la résa ou au pricing_block.
//
// Lookup (kind-aware) :
//   - kind='reservation'   → inbox_messages WHERE reservation_id = $id
//     (priorité) OR (reservation_id IS NULL AND sender_email = $guest_email)
//   - kind='pricing_block' → inbox_messages WHERE pricing_block_id = $id
//     (FK posée PR #169b ADR-106)
//
// Composer : ResaMessageComposerWrapper (PR #170 ADR-107 Option Y) gère sélection
// canal + envoi via /api/send-email type=inbox_reply. Empty state si aucun canal.
//
// Optimistic-insert (H-2) : onSent({externalId, messageId}) → push immédiat
// outbound dans setMessages sans refetch (UX instantané).

function MessagesTab({ item }) {
  const r = item.shape
  const itemKind = item.kind || 'reservation'
  const isBlock = itemKind === 'pricing_block'
  const [messages, setMessages] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      setLoading(true); setError(null)
      try {
        let query = supabase
          .from('inbox_messages')
          .select('*')
          .order('created_at', { ascending: true })
          .limit(200)
        if (isBlock) {
          // PR #170 — lookup pricing_block via FK pricing_block_id (ADR-106).
          query = query.eq('pricing_block_id', r.id)
        } else if (r.guest_email) {
          query = query.or(`reservation_id.eq.${r.id},and(reservation_id.is.null,sender_email.eq.${r.guest_email})`)
        } else {
          query = query.eq('reservation_id', r.id)
        }
        const { data, error: e } = await query
        if (cancelled) return
        if (e) throw e
        setMessages(data || [])
      } catch (e) { if (!cancelled) setError(e.message) }
      finally { if (!cancelled) setLoading(false) }
    })()
    return () => { cancelled = true }
  }, [r.id, r.guest_email, isBlock])

  // PR #170 — dernier message inbound pour threading email (Re: + In-Reply-To).
  // Lot « conversation enrichie » — GARDE (b) : une entrée canal='form_comment'
  // (commentaire du formulaire de bienvenue) n'est JAMAIS « répondable » comme
  // un message plateforme — elle ne devient pas le lastInbound du composer.
  // Même règle pour canal='depart_feedback' (ressenti de fin de séjour,
  // système d'avis « option a ») : la réponse part comme un message NEUF.
  const lastInbound = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (
        messages[i].direction === 'inbound' &&
        messages[i].canal !== 'form_comment' &&
        messages[i].canal !== 'depart_feedback'
      )
        return messages[i]
    }
    return null
  }, [messages])

  // H-2 optimistic-insert — push outbound local immédiat sans refetch.
  // BUG 38 (multi-canal) : onSent reçoit désormais { results, canaux, body } où
  // results = [{canal, status, externalId?, messageId?}]. On insère UNE bulle
  // outbound locale par canal effectivement abouti (status sent ou sent_dry_run),
  // imitant l'INSERT backend par canal (api/send-email.js).
  function handleSent({ results, body } = {}) {
    const delivered = Array.isArray(results)
      ? results.filter(r => r.status === 'sent' || r.status === 'sent_dry_run')
      : []
    if (delivered.length === 0) return
    const nowIso = new Date().toISOString()
    setMessages(prev => [
      ...prev,
      ...delivered.map((d) => ({
        id: `optimistic-${d.externalId || crypto.randomUUID()}`,
        direction: 'outbound',
        canal: d.canal || prev[prev.length - 1]?.canal || 'email',
        thread_id: prev[prev.length - 1]?.thread_id || null,
        reservation_id: isBlock ? null : r.id,
        pricing_block_id: isBlock ? r.id : null,
        sender_name: 'FULLBNBZEN (Inbox composer)',
        body: body || '(message envoyé à l\'instant)',
        subject: null,
        external_id: d.externalId || null,
        created_at: nowIso,
        metadata: {
          source: 'resa_v2_modal',
          message_id: d.messageId || null,
          ...(d.status === 'sent_dry_run' ? { dry_run: true } : {}),
        },
      })),
    ])
  }

  return (
    <div style={{display:"flex",flexDirection:"column",flex:1,minHeight:300}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",marginBottom:14,flexWrap:"wrap",gap:10}}>
        <div style={{fontFamily:DS.fontDisplay,fontSize:16,fontWeight:700,color:DS.navy}}>
          Conversation ({messages.length})
        </div>
      </div>

      {/* Lot « conversation enrichie » — timeline des envois automatiques
          (moteur Plateformes, message_sends) pour CETTE résa. Résas OTA
          uniquement : un pricing_block (direct) n'est jamais évalué par le
          moteur Plateformes (ADR-266). */}
      {!isBlock && <ResaJourneyTimeline reservationId={r.id} />}

      {error && <div style={{padding:"10px 12px",background:"#fdf2f2",color:DS.red,borderRadius:8,fontSize:12,marginBottom:10}}>{error}</div>}
      {loading && <div style={{padding:24,textAlign:"center",color:"#888",fontSize:12}}>Chargement…</div>}

      {!loading && !error && (
        <div style={{flex:1,minHeight:280,display:"flex",flexDirection:"column",border:"1px solid #e8edf4",borderRadius:8,overflow:"hidden"}}>
          <ThreadDisplay
            messages={messages}
            emptyMessage="Aucune conversation pour le moment. Composez le premier message ci-dessous."
          />
          {/* PR #170 (ADR-107) — composer multi-canal réutilisant InboxComposer. */}
          <ResaMessageComposerWrapper
            item={item}
            itemKind={itemKind}
            lastInbound={lastInbound}
            onSent={handleSent}
          />
        </div>
      )}
    </div>
  )
}

// ── Atomes ─────────────────────────────────────────────────────────

function SectionTitle({ icon: Icon, children }) {
  return (
    <div style={{display:"flex",alignItems:"center",gap:7,fontFamily:DS.fontDisplay,fontSize:14,fontWeight:600,color:DS.marineDark,marginBottom:12}}>
      {Icon && <Icon size={15} strokeWidth={2.2} color={DS.marine} />} {children}
    </div>
  )
}

// PR #169a v1.4 (ADR-104 Scope B) — atome édition inline 4 champs internes.
function EditableRow({ label, testid, value, onChange, disabled, type = "text" }) {
  return (
    <div style={{display:"flex",padding:"6px 0",borderBottom:"1px solid #f0f4f8",fontSize:13,gap:10,alignItems:"center"}}>
      <div style={{flex:"0 0 130px",color:"#888",fontWeight:500}}>{label}</div>
      <div style={{flex:1}}>
        <input
          type={type}
          data-testid={testid}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          maxLength={200}
          style={{width:"100%",padding:"5px 8px",border:"1px solid #dde3ec",borderRadius:5,fontSize:12,fontFamily:DS.fontBody,background:"#fff"}}
        />
      </div>
    </div>
  )
}

function DataRow({ label, value, isLink }) {
  const isEmpty = value == null || value === '' || value === '—'
  let displayValue
  if (isEmpty) {
    displayValue = <span style={{color:"#aaa",fontStyle:"italic"}}>—</span>
  } else if (isLink === 'mailto') {
    displayValue = <a href={`mailto:${value}`} style={{color:DS.skyDeep,textDecoration:"none"}}>{value}</a>
  } else if (isLink === 'tel') {
    displayValue = <a href={`tel:${value}`} style={{color:DS.skyDeep,textDecoration:"none"}}>{value}</a>
  } else {
    displayValue = value
  }
  return (
    <div style={{display:"flex",padding:"6px 0",borderBottom:"1px solid #f0f4f8",fontSize:13,gap:10}}>
      <div style={{flex:"0 0 130px",color:"#888",fontWeight:500}}>{label}</div>
      <div style={{flex:1,color:"#333",wordBreak:"break-word"}}>{displayValue}</div>
    </div>
  )
}

// ── FichesArriveeTab — Fiches d'arrivée LOT 3 (ADR-188) ──────────────────────
// Affiche, en LECTURE SEULE admin, les fiches d'arrivée soumises par le voyageur
// pour l'item ouvert (réservation OTA = reservations.id, ou résa direct =
// pricing_blocks.id). form_tokens.reservation_id stocke l'id de l'item ouvert
// (col unique, pas de discriminant kind) → on requête par item.id quel que soit
// le kind. Pour chaque token (unified/welcome/checkin) on charge les submissions
// (durcies #522 : limit 50, sans ip_address) et on rend le payload via
// FormSubmissionView (échappement strict, jamais d'HTML brut voyageur).
//
// Les libellés des champs sont résolus depuis le formulaire configuré du logement
// (cfg.props[].logs[].forms.unified.fields[], même source que FormulairesTab) —
// on passe `fields` à FormSubmissionView pour afficher les labels métier.
//
// Pièce d'identité — N2-IA LOT 2 (ADR-189) : on affiche l'ÉTAT RÉEL des pièces
// depuis id_documents (résolu par token_id), avec un badge par statut. Fallback
// rétrocompat : si aucune ligne id_documents (migration inerte / fiche legacy),
// on retombe sur l'ancien affichage du nom de fichier porté par le payload.
//
// Deux espaces de noms de refs cohabitent :
//   - payload (formulaire public)  : passport_photo / id_recto / id_verso
//   - id_documents (table pivot)   : passport / cni_recto / cni_verso
// PAYLOAD_REF → DOC_REF fait le pont pour le fallback ; les badges réels
// raisonnent en DOC_REF (la source d'autorité = id_documents).
const ID_DOC_PAYLOAD_REFS = ['passport_photo', 'id_recto', 'id_verso']
const PAYLOAD_REF_TO_DOC_REF = {
  passport_photo: 'passport',
  id_recto: 'cni_recto',
  id_verso: 'cni_verso',
}
// Libellés métier par ref id_documents (source d'autorité).
const ID_DOC_REF_LABELS = {
  passport: "Passeport",
  cni_recto: "Pièce d'identité — recto",
  cni_verso: "Pièce d'identité — verso",
}
// Libellés du fallback stub (espace payload).
const ID_DOC_PAYLOAD_LABELS = {
  passport_photo: "Page d'identité du passeport",
  id_recto: "Pièce d'identité — recto",
  id_verso: "Pièce d'identité — verso",
}

// Badge par status id_documents. Couleurs DS-cohérentes, sémantique claire.
// POSTURE M8 (ADR-307) : les badges disent « analyse automatique », JAMAIS
// « vérifié » — le vert ne dispense d'AUCUN contrôle humain sur le chemin
// caution/contrat (pré-tri d'attention, pas une authentification).
const ID_DOC_STATUS_BADGE = {
  received:   { label: 'Reçue', icon: Inbox, color: DS.marineDeep, bg: DS.cielSoft, border: `${DS.ciel}66` },
  processing: { label: 'Vérification en cours', icon: Loader2, color: DS.warningText, bg: DS.warningBg, border: DS.warning },
  verified:   { label: 'Analyse auto : OK', icon: CheckCircle, color: DS.successText, bg: DS.successBg, border: `${DS.success}66` },
  failed:     { label: 'Analyse auto : à reprendre', icon: AlertTriangle, color: DS.dangerText, bg: DS.dangerBg, border: `${DS.danger}44` },
}
// Badge d'une pièce PURGÉE (lot RGPD : deleted_at posé par le cron 14 j —
// l'objet Storage n'existe plus). PRIME sur le status résiduel : afficher
// « Vérifiée » sur une pièce détruite serait trompeur ; Voir/Relancer masqués.
const ID_DOC_PURGED_BADGE = {
  label: 'Purgé (RGPD)', icon: XCircle, color: DS.textSecondary || '#6b7280', bg: DS.pageBg || '#f3f4f6', border: DS.border || '#d1d5db',
}
// Badge informatif name_match (présent seulement si la pièce a été vérifiée).
const NAME_MATCH_BADGE = {
  match:    { label: 'Nom concordant', icon: CheckCircle, color: DS.successText, bg: DS.successBg, border: `${DS.success}66` },
  mismatch: { label: 'Nom divergent', icon: XCircle, color: DS.dangerText, bg: DS.dangerBg, border: `${DS.danger}44` },
  unknown:  { label: 'Nom illisible', icon: HelpCircle, color: DS.textSecondary, bg: DS.pageBg, border: DS.border },
}

// Extrait un nom de fichier lisible d'une valeur de champ fichier (string nom,
// objet { name }, ou fallback générique). Aucune valeur non-fiable n'est rendue
// en HTML — FormSubmissionView escape, ici on borne juste à du texte.
function idDocFileName(value) {
  if (value == null || value === '') return null
  if (typeof value === 'string') return value
  if (typeof value === 'object' && typeof value.name === 'string' && value.name) return value.name
  if (typeof value === 'object' && typeof value.filename === 'string' && value.filename) return value.filename
  return 'Fichier joint'
}

// ── Section « Contrat signé » (micro-lot Documents) ─────────────────────────
// Affiche l'état du contrat signé du parcours direct et un bouton « Voir » qui
// ouvre le PDF via une signed URL TTL 120s (action admin contract_download_url ;
// path dérivé serveur de la DBR — double préfixe contracts/<id>/<sig>.pdf #544).
//
// On sonde l'état au montage : un appel contract_download_url renvoie null si le
// contrat n'est pas (encore) signé/archivé (→ état « absent »), une URL sinon.
// L'URL n'est PAS pré-chargée pour être affichée : on la (re)demande au clic
// « Voir » afin de toujours ouvrir un lien frais (TTL court). La sonde initiale
// sert uniquement à savoir si le bouton doit être proposé.
//
// Disponible aussi sur une résa ANNULÉE (lecture seule, conservation légale ~5
// ans) : aucune dépendance au statut côté UI ; le serveur sert le PDF tant que
// la DBR existe et que le contrat est archivé.
export function ContractDocSection({ item }) {
  const [state, setState] = useState('probing') // 'probing' | 'present' | 'absent' | 'error'
  const [busy, setBusy] = useState(false)
  const [actionError, setActionError] = useState(null)

  const itemId = item?.id || null
  const itemKind = item?.kind || 'reservation'

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      if (!itemId) { if (!cancelled) setState('absent'); return }
      setState('probing'); setActionError(null)
      try {
        const url = await contractDownloadUrl(itemId, itemKind)
        if (cancelled) return
        setState(url ? 'present' : 'absent')
      } catch (e) {
        if (cancelled) return
        // Service indisponible / pas de contrat → on dégrade en « absent » plutôt
        // que de bloquer toute la section Documents (best-effort, cohérent id-docs).
        setState('absent')
      }
    })()
    return () => { cancelled = true }
  }, [itemId, itemKind])

  async function handleView() {
    if (busy) return
    setBusy(true); setActionError(null)
    try {
      const url = await contractDownloadUrl(itemId, itemKind)
      if (!url) { setState('absent'); return }
      window.open(url, '_blank', 'noopener,noreferrer')
    } catch (e) {
      setActionError(e.message || String(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div data-testid="contract-doc-section" style={{ marginBottom: 16 }}>
      <SectionTitle icon={FileText}>Contrat signé</SectionTitle>
      <div style={{ border: '1px solid #e8edf4', borderRadius: 8, padding: 14, background: DS.white }}>
        {state === 'probing' && (
          <div style={{ fontSize: 12, color: '#888' }}>Chargement du contrat…</div>
        )}
        {state === 'absent' && (
          <div data-testid="contract-doc-absent" style={{ fontSize: 12, color: '#999' }}>
            Aucun contrat signé n'est archivé pour cette réservation.
          </div>
        )}
        {state === 'present' && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ fontSize: 13, color: DS.navy, fontWeight: 600 }}>
              Contrat de mise à disposition signé (PDF)
            </div>
            <button
              data-testid="contract-doc-view"
              onClick={handleView}
              disabled={busy}
              style={{ background: DS.navy, color: '#fff', border: 'none', borderRadius: 7, padding: '7px 14px', fontSize: 12, fontWeight: 700, cursor: busy ? 'not-allowed' : 'pointer', opacity: busy ? 0.5 : 1, fontFamily: DS.fontBody }}
            >
              {busy ? '…' : 'Voir'}
            </button>
          </div>
        )}
        {actionError && (
          <div data-testid="contract-doc-error" style={{ marginTop: 8, fontSize: 11, color: DS.red }}>
            {actionError}
          </div>
        )}
      </div>
    </div>
  )
}

// ── Section « État des lieux » — CHANTIER EDL (lecture seule v1) ─────────────
// Soumission d'état des lieux d'arrivée du voyageur (edl_submissions, clé =
// item.id polymorphique reservations/pricing_blocks — même convention que
// form_tokens.reservation_id). Affiche : type (rien à signaler / signalement),
// datage, description, photos (signed URL TTL 120s — RE-DEMANDÉE au clic pour
// toujours ouvrir un lien frais, jamais persistée), purge_after + verrou litige.
// LECTURE SEULE v1 : pas d'édition, pas de prolongation (lot séparé).
export function EdlAdminSection({ item }) {
  const [state, setState] = useState('loading') // 'loading' | 'ready' | 'inactive' | 'error'
  const [submission, setSubmission] = useState(null)
  const [busyPhoto, setBusyPhoto] = useState(null) // index photo en cours d'ouverture
  const [actionError, setActionError] = useState(null)

  const itemId = item?.id || null

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      if (!itemId) { if (!cancelled) { setSubmission(null); setState('ready') } ; return }
      setState('loading'); setActionError(null)
      try {
        const r = await edlAdminGet(itemId)
        if (cancelled) return
        if (r.unavailable) { setState('inactive'); return }
        setSubmission(r.submission)
        setState('ready')
      } catch (e) {
        if (cancelled) return
        setState('error')
      }
    })()
    return () => { cancelled = true }
  }, [itemId])

  // Ouvre la photo i avec une URL FRAÎCHE (TTL 120s : celle du mount peut être
  // expirée au moment du clic — on re-signe systématiquement).
  async function handleOpenPhoto(i) {
    if (busyPhoto != null) return
    setBusyPhoto(i); setActionError(null)
    try {
      const r = await edlAdminGet(itemId)
      const url = r.submission?.photos?.[i]?.signed_url
      if (!url) { setActionError('Photo indisponible (objet manquant ou purgé).'); return }
      window.open(url, '_blank', 'noopener,noreferrer')
    } catch (e) {
      setActionError(e.message || String(e))
    } finally {
      setBusyPhoto(null)
    }
  }

  const typeLabel = submission?.type === 'rien_a_signaler'
    ? 'Rien à signaler'
    : 'Signalement'
  const typeColors = submission?.type === 'rien_a_signaler'
    ? { color: DS.successText, bg: DS.successBg, border: `${DS.success}66` }
    : { color: DS.warningText, bg: DS.warningBg, border: DS.warning }

  return (
    <div data-testid="edl-admin-section" style={{ marginBottom: 16 }}>
      <SectionTitle icon={ClipboardList}>État des lieux d'arrivée</SectionTitle>
      <div style={{ border: '1px solid #e8edf4', borderRadius: 8, padding: 14, background: DS.white }}>
        {state === 'loading' && (
          <div style={{ fontSize: 12, color: '#888' }}>Chargement de l'état des lieux…</div>
        )}
        {state === 'inactive' && (
          <div data-testid="edl-admin-inactive" style={{ fontSize: 12, color: '#999' }}>
            Module état des lieux inactif (migration non appliquée).
          </div>
        )}
        {state === 'error' && (
          <div data-testid="edl-admin-error" style={{ fontSize: 12, color: DS.red }}>
            Impossible de charger l'état des lieux.
          </div>
        )}
        {state === 'ready' && !submission && (
          <div data-testid="edl-admin-absent" style={{ fontSize: 12, color: '#999' }}>
            Aucun état des lieux soumis par le voyageur pour cette réservation.
          </div>
        )}
        {state === 'ready' && submission && (
          <div data-testid="edl-admin-submission">
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 8 }}>
              <span
                data-testid="edl-admin-type"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 999, color: typeColors.color, background: typeColors.bg, border: `1px solid ${typeColors.border}` }}
              >
                {typeLabel}
              </span>
              <span style={{ fontSize: 11, color: '#999' }}>
                Soumis le {submission.submitted_at ? formatDateTimeFR(submission.submitted_at) : '—'}
              </span>
              {submission.locked && (
                <span data-testid="edl-admin-locked" style={{ fontSize: 11, fontWeight: 700, color: DS.dangerText, background: DS.dangerBg, border: `1px solid ${DS.danger}44`, padding: '3px 9px', borderRadius: 999 }}>
                  🔒 Litige — purge gelée
                </span>
              )}
            </div>
            {submission.description && (
              <div data-testid="edl-admin-description" style={{ fontSize: 13, color: '#333', background: '#f7f9fc', borderRadius: 6, padding: '8px 10px', marginBottom: 10, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {submission.description}
              </div>
            )}
            {submission.photos_count > 0 && (
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
                {Array.from({ length: submission.photos_count }, (_, i) => (
                  <button
                    key={i}
                    data-testid={`edl-admin-photo-${i}`}
                    onClick={() => handleOpenPhoto(i)}
                    disabled={busyPhoto != null}
                    style={{ background: DS.navy, color: '#fff', border: 'none', borderRadius: 7, padding: '6px 12px', fontSize: 12, fontWeight: 700, cursor: busyPhoto != null ? 'not-allowed' : 'pointer', opacity: busyPhoto === i ? 0.5 : 1 }}
                  >
                    {busyPhoto === i ? '…' : `📷 Photo ${i + 1}`}
                  </button>
                ))}
              </div>
            )}
            <div style={{ fontSize: 11, color: '#999' }}>
              Conservation jusqu'au {submission.purge_after ? formatDateTimeFR(submission.purge_after) : '—'} (90 jours, prolongeable en cas de litige).
            </div>
          </div>
        )}
        {actionError && (
          <div data-testid="edl-admin-action-error" style={{ marginTop: 8, fontSize: 11, color: DS.red }}>
            {actionError}
          </div>
        )}
      </div>
    </div>
  )
}

export function FichesArriveeTab({ item, cfg }) {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  // entries : [{ token, type, submissions:[{ id, payload, submitted_at }] }]
  const [entries, setEntries] = useState([])
  // idDocsByToken : { [token_id]: [{ ref, status, name_match, fail_reason }] }
  // N2-IA LOT 2 — état réel des pièces d'identité, résolu par token_id.
  const [idDocsByToken, setIdDocsByToken] = useState({})
  // Force un re-fetch des id_documents après « Relancer la vérification ».
  const [idDocsReloadKey, setIdDocsReloadKey] = useState(0)

  // Champs du formulaire configuré du logement (résolution labels). Même source
  // que FormulairesTab : migrateLegacyToUnified(log.forms).fields.
  const fields = useMemo(() => {
    const log = findLogementInCfg(cfg, item?.shape?.logement_id)
    if (!log) return []
    const unified = migrateLegacyToUnified(log.forms || {})
    return Array.isArray(unified.fields) ? unified.fields : []
  }, [cfg, item])

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      setLoading(true); setError(null)
      try {
        if (!item?.id) {
          if (!cancelled) { setEntries([]); setLoading(false) }
          return
        }
        const tokens = await listFormTokensByReservation(item.id)
        const withSubs = await Promise.all(
          tokens.map(async (t) => {
            const submissions = await listFormSubmissions(t.token)
            return { token: t.token, type: t.type, created_at: t.created_at, submissions }
          }),
        )
        if (!cancelled) setEntries(withSubs)

        // N2-IA LOT 2 — état réel des pièces d'identité par token. Best-effort :
        // un échec (table absente / RLS) ne casse pas l'onglet → fallback stub.
        try {
          const docs = await listIdDocumentsByTokens(tokens.map((t) => t.token))
          if (!cancelled) {
            const byToken = {}
            for (const d of docs) {
              ;(byToken[d.token_id] = byToken[d.token_id] || []).push(d)
            }
            setIdDocsByToken(byToken)
          }
        } catch (_) {
          if (!cancelled) setIdDocsByToken({})
        }
      } catch (e) {
        if (!cancelled) setError(e.message || String(e))
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [item, idDocsReloadKey])

  // Token le plus récent (tokens triés desc created_at) pour le bouton "Copier le lien".
  // CHANTIER LIEN COURT — la copie passe désormais par CopyGuestLinkButton
  // (kind `form`) : lien court `/l/<code>` émis serveur, REPLI automatique sur
  // le lien long `/bienvenue/<token>` si l'émission échoue. `publicFormUrl`
  // reste la source du lien CLIQUABLE de l'onglet Formulaires (autre fichier).
  const latestToken = entries.length > 0 ? entries[0].token : null

  if (loading) {
    return <div style={{ fontSize: 13, color: '#888', padding: 12 }}>Chargement des fiches…</div>
  }
  if (error) {
    return (
      <div data-testid="fiches-error" style={{ padding: '10px 12px', background: '#fce8e8', color: DS.red, borderRadius: 6, fontSize: 12 }}>
        {error}
      </div>
    )
  }

  const totalSubmissions = entries.reduce((n, e) => n + e.submissions.length, 0)

  if (totalSubmissions === 0) {
    return (
      <div data-testid="documents-tab">
        {/* Contrat signé — toujours rendu (présent/absent), même sans fiche
            soumise (ex. résa annulée conservée). */}
        <ContractDocSection item={item} />
        {/* État des lieux voyageur (EDL) — lecture seule, toujours rendu. */}
        <EdlAdminSection item={item} />
        <SectionTitle icon={IdCard}>Pièces d'identité &amp; fiche d'arrivée</SectionTitle>
        {latestToken && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
            <CopyGuestLinkButton kind="form" token={latestToken} label="Copier le lien du formulaire" testid="copy-form-link" />
          </div>
        )}
        <EmptyTabPlaceholder
          icon={ClipboardList}
          title="Aucune fiche soumise"
          message={
            latestToken
              ? "Un lien de fiche d'arrivée a été généré mais le voyageur n'a encore rien soumis."
              : "Aucun lien de fiche d'arrivée n'est rattaché à cette réservation. Génère-le depuis l'onglet Formulaires du logement."
          }
        />
      </div>
    )
  }

  return (
    <div data-testid="documents-tab">
      {/* Contrat signé — section dédiée en tête de l'onglet Documents. */}
      <ContractDocSection item={item} />
      {/* État des lieux voyageur (EDL) — lecture seule, toujours rendu. */}
      <EdlAdminSection item={item} />
      <div data-testid="fiches-arrivee-tab">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <SectionTitle icon={IdCard}>Pièces d'identité &amp; fiche d'arrivée</SectionTitle>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <RouvrirFormulaireButton reservationId={item?.id} />
          {latestToken && <CopyGuestLinkButton kind="form" token={latestToken} label="Copier le lien du formulaire" testid="copy-form-link" />}
        </div>
      </div>

      {entries.map((entry) =>
        entry.submissions.map((sub) => (
          <div
            key={sub.id}
            style={{ border: '1px solid #e8edf4', borderRadius: 8, padding: 14, marginBottom: 12, background: DS.white }}
          >
            <div style={{ fontSize: 11, color: '#999', marginBottom: 10, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <span>Soumise le {sub.submitted_at ? formatDateTimeFR(sub.submitted_at) : '—'}</span>
              <span style={{ color: '#bbb' }}>•</span>
              <span style={{ fontFamily: 'monospace' }}>token {String(entry.token).slice(0, 8)}…</span>
            </div>

            <FormSubmissionView payload={sub.payload} fields={fields} emptyLabel="Fiche vide." />

            <IdDocumentSection
              payload={sub.payload}
              docs={idDocsByToken[entry.token] || []}
              onRechecked={() => setIdDocsReloadKey((k) => k + 1)}
            />
          </div>
        )),
      )}
      </div>
    </div>
  )
}

// « Rouvrir » la fiche d'arrivée (décision Dimitri 03/10). Le voyageur modifie
// sa fiche jusqu'à son arrivée ; après, elle est figée. Ce bouton la rouvre
// pour 48 h (action admin form_rouvrir). Le SERVEUR décide : si la fiche est
// encore modifiable, il n'écrit rien et dit jusqu'à quand.
// Une phrase en français simple par refus du serveur (relectures du 28/10,
// point 5) — jamais un code technique à l'écran.
const ROUVRIR_MESSAGES = {
  aucune_fiche: "Le voyageur n'a encore rien envoyé : il n'y a rien à rouvrir.",
  lien_expire: "Le lien du formulaire a expiré : le voyageur ne pourrait plus l'ouvrir. Envoyez-lui un nouveau lien.",
  reservation_annulee: "Réservation annulée : la fiche n'est pas rouverte.",
  migration_absente: "Pas encore disponible : la colonne en base n'a pas encore été ajoutée (migration à appliquer).",
  lecture_impossible: "Lecture impossible pour le moment. Réessayez dans un instant.",
  ecriture_impossible: "Enregistrement impossible. Réessayez dans un instant.",
  fiche_introuvable: "La fiche a changé entre-temps : rien n'a été rouvert. Réessayez.",
  not_authenticated: "Session expirée : reconnectez-vous.",
  unauthorized: "Session expirée : reconnectez-vous.",
  'Auth requise': "Session expirée : reconnectez-vous.",
  forbidden: "Réservé à l'administrateur.",
}
export function messageRouvrirErreur(code) {
  return (typeof code === 'string' && ROUVRIR_MESSAGES[code]) || 'Réouverture impossible. Réessayez dans un instant.'
}

function RouvrirFormulaireButton({ reservationId }) {
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState(null) // { ok: boolean, text: string }
  if (!reservationId) return null
  async function onClick() {
    setBusy(true); setMsg(null)
    try {
      const r = await rouvrirFormulaire(reservationId)
      if (r.deja_modifiable) {
        // avec_heure=false : logement sans heure d'arrivée configurée — la
        // limite est le début du jour d'arrivée, aucune heure n'est inventée.
        setMsg({ ok: true, text: r.avec_heure === false
          ? `Déjà modifiable par le voyageur jusqu'à la veille de son arrivée.`
          : `Déjà modifiable par le voyageur jusqu'au ${r.jusqu_au ? formatDateTimeFR(r.jusqu_au) : '—'} (heure de Paris).` })
      } else {
        setMsg({ ok: true, text: `Rouvert au voyageur jusqu'au ${r.rouvert_jusqu_au ? formatDateTimeFR(r.rouvert_jusqu_au) : '—'}.` })
      }
    } catch (e) {
      setMsg({ ok: false, text: messageRouvrirErreur(e && (e.code || e.message)) })
    } finally {
      setBusy(false)
    }
  }
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
      <button
        type="button"
        data-testid="rouvrir-formulaire"
        onClick={onClick}
        disabled={busy}
        style={{ fontSize: 12, fontWeight: 600, padding: '6px 10px', borderRadius: 6, border: `1px solid ${DS.border || '#d1d5db'}`, background: DS.white, color: DS.navy || '#1e3a5f', cursor: busy ? 'wait' : 'pointer' }}
      >
        {busy ? 'Réouverture…' : 'Rouvrir au voyageur (48 h)'}
      </button>
      {msg && (
        <span data-testid="rouvrir-formulaire-msg" style={{ fontSize: 11, color: msg.ok ? (DS.successText || '#166534') : (DS.dangerText || DS.red) }}>
          {msg.text}
        </span>
      )}
    </span>
  )
}

// Pastille générique (status / name_match). Texte borné, jamais d'HTML brut.
function IdDocBadge({ spec, testid }) {
  if (!spec) return null
  return (
    <span
      data-testid={testid}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 10, fontWeight: 700, color: spec.color, background: spec.bg, border: `1px solid ${spec.border}`, borderRadius: 7, padding: '2px 8px', whiteSpace: 'nowrap' }}
    >
      {spec.icon && <spec.icon size={11} strokeWidth={2.3} />}{spec.label}
    </span>
  )
}

// Ligne d'une pièce d'identité RÉELLE (source id_documents) : libellé + badge de
// statut + (si vérifiée) badge name_match + bouton « Voir » (signed URL TTL 120s)
// + bouton « Relancer » (sur 'failed' uniquement).
function IdDocRealRow({ doc, onRechecked }) {
  const [busy, setBusy] = useState(null) // 'view' | 'recheck' | null
  const [actionError, setActionError] = useState(null)
  // Pièce PURGÉE (lot RGPD, deleted_at posé par le cron 14 j) : badge dédié qui
  // PRIME sur le status résiduel ('verified'…), bouton « Voir » masqué (l'objet
  // Storage n'existe plus — un clic donnerait un 404 trompeur), « Relancer »
  // masqué aussi (rien à re-vérifier).
  const isPurged = !!doc.deleted_at
  const statusSpec = isPurged ? ID_DOC_PURGED_BADGE : (ID_DOC_STATUS_BADGE[doc.status] || null)
  const matchSpec = !isPurged && doc.name_match ? (NAME_MATCH_BADGE[doc.name_match] || null) : null

  async function handleView() {
    setActionError(null); setBusy('view')
    try {
      const url = await idDocDownloadUrl(doc.token_id, doc.ref)
      window.open(url, '_blank', 'noopener,noreferrer')
    } catch (e) {
      setActionError("Lien indisponible.")
    } finally {
      setBusy(null)
    }
  }

  async function handleRecheck() {
    setActionError(null); setBusy('recheck')
    try {
      await idDocRecheck(doc.token_id, doc.ref)
      if (onRechecked) onRechecked()
    } catch (e) {
      setActionError("Relance impossible.")
      setBusy(null)
    }
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }} data-testid="id-doc-real-row">
      <span style={{ fontSize: 12, color: '#555', fontWeight: 600 }}>
        {ID_DOC_REF_LABELS[doc.ref] || doc.ref}
      </span>
      <IdDocBadge spec={statusSpec} testid="id-doc-status-badge" />
      {matchSpec && <IdDocBadge spec={matchSpec} testid="id-doc-match-badge" />}
      {!isPurged && doc.status === 'failed' && doc.fail_reason && (
        <span data-testid="id-doc-fail-reason" style={{ fontSize: 11, color: '#9a2820' }}>
          ({String(doc.fail_reason)})
        </span>
      )}
      {!isPurged && (
        <button
          onClick={handleView}
          disabled={busy === 'view'}
          data-testid="id-doc-view-btn"
          style={{ fontSize: 11, fontWeight: 600, color: DS.navy, background: '#eef2f7', border: 'none', borderRadius: 6, padding: '3px 9px', cursor: busy === 'view' ? 'default' : 'pointer', opacity: busy === 'view' ? 0.6 : 1 }}
        >
          {busy === 'view' ? '…' : 'Voir'}
        </button>
      )}
      {!isPurged && doc.status === 'failed' && (
        <button
          onClick={handleRecheck}
          disabled={busy === 'recheck'}
          data-testid="id-doc-recheck-btn"
          style={{ fontSize: 11, fontWeight: 600, color: '#7c5e00', background: '#fff3cd', border: '1px solid #ffe08a', borderRadius: 6, padding: '3px 9px', cursor: busy === 'recheck' ? 'default' : 'pointer', opacity: busy === 'recheck' ? 0.6 : 1 }}
        >
          {busy === 'recheck' ? '…' : 'Relancer la vérification'}
        </button>
      )}
      {actionError && (
        <span data-testid="id-doc-action-error" style={{ fontSize: 11, color: DS.red }}>{actionError}</span>
      )}
    </div>
  )
}

// Section "Pièce d'identité" — N2-IA LOT 2 (ADR-189).
//   - Si des lignes id_documents existent pour ce token → on affiche l'ÉTAT RÉEL
//     (badge par statut + name_match + boutons Voir / Relancer).
//   - Sinon (migration inerte / fiche legacy) → fallback rétrocompat : nom de
//     fichier porté par le payload + pastille placeholder.
function IdDocumentSection({ payload, docs, onRechecked }) {
  const realDocs = Array.isArray(docs) ? docs : []

  // ── Cas nominal : état réel id_documents ──────────────────────────────────
  if (realDocs.length > 0) {
    return (
      <div data-testid="id-doc-section" style={{ marginTop: 12, paddingTop: 12, borderTop: '1px dashed #e0e6ef' }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: DS.navy, marginBottom: 8 }}>Pièce d'identité</div>
        {realDocs.map((d) => (
          <IdDocRealRow key={d.ref} doc={d} onRechecked={onRechecked} />
        ))}
      </div>
    )
  }

  // ── Fallback rétrocompat : fichiers portés par le payload ─────────────────
  const safe = payload && typeof payload === 'object' && !Array.isArray(payload) ? payload : {}
  const stubDocs = ID_DOC_PAYLOAD_REFS
    .map((ref) => ({ ref, name: idDocFileName(safe[ref]) }))
    .filter((d) => d.name != null)

  if (stubDocs.length === 0) return null

  return (
    <div data-testid="id-doc-section" style={{ marginTop: 12, paddingTop: 12, borderTop: '1px dashed #e0e6ef' }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: DS.navy, marginBottom: 8 }}>Pièce d'identité</div>
      {stubDocs.map((d) => (
        <div key={d.ref} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 12, color: '#555' }}>
            {ID_DOC_PAYLOAD_LABELS[d.ref] || d.ref} : <strong style={{ color: '#222', wordBreak: 'break-all' }}>{String(d.name)}</strong>
          </span>
          <span
            data-testid="id-doc-ia-badge"
            style={{ fontSize: 10, fontWeight: 700, color: '#7c5e00', background: '#fff3cd', border: '1px solid #ffe08a', borderRadius: 999, padding: '2px 8px' }}
          >
            Vérification IA à venir
          </span>
        </div>
      ))}
    </div>
  )
}

// ── ContratTab — Phase 6.D PR #169a v1.4 (ADR-104) + PR #169a-bis (ADR-105) ───
// Onglet Contrat actif :
//   - kind='reservation' AND source='direct' (PR #168 D-6/D-7)
//   - kind='pricing_block' AND guest_first_name NOT NULL (Q-D8)
// Le filtrage est fait par getEffectiveTabs — ce composant ne gère QUE le rendu.
// Pour pricing_block sans guest_email : EmptyTabPlaceholder (bouton envoi désactivé).
//
// Pattern : pré-fill HTML via genContratHTML(log, reservation, cfg, prop, mode='preview')
// affiché dans iframe. Boutons Aperçu/Imprimer (window.open + print) +
// Envoyer voyageur (POST /api/send-email type='contrat_send').
//
// Clauses lues depuis cfg.props[].logs[].contrat_clauses (fallback DEFAULT_CLAUSES
// dans genContratHTML). Pour sous-loc Maison Zen : cfg.props[p6].logs[].chambres[].contrat_clauses
// si défini sur la chambre interne.
//
// PR #169a-bis prop `itemKind` permet de différencier le code de paramétrage
// pour résa OTA direct vs pricing_block direct.

// PR #169a-bis Commit 5 — exporté pour tests unitaires (Q-D11 Option 2).
export function ContratTab({ r, itemKind, logementInfo, cfg }) {
  const [html, setHtml] = useState("")
  const [busy, setBusy] = useState(false)
  const [info, setInfo] = useState(null)
  const [error, setError] = useState(null)

  const log = logementInfo || (cfg ? findLogementInCfg(cfg, r.logement_id) : null)
  const prop = logementInfo?.prop || null
  const isBlock = itemKind === 'pricing_block'

  // CONTRAT V2 (revue M1) — verdict caution câblé sur la dérivation unique
  // (miroir client resolveContratCautionDueClient, plateforme 'direct') :
  // 1er rendu immédiat SANS verdict (undefined = historique), re-rendu dès que
  // le verdict arrive. Résolution impossible → undefined → rendu historique.
  // Nom propre du logement (30/09, même règle que les factures) : sans nom,
  // aucun contrat n'est généré — jamais « lc2 », jamais le nom de la maison.
  const nomContrat = nomLogementFacture(log)

  useEffect(() => {
    if (!log) return
    if (!nomContrat) { setHtml(""); setError(RAISON_CONTRAT_SANS_NOM); return }
    let mounted = true
    const render = (cautionDue) => {
      try {
        const { html: generatedHtml } = genContratHTML({
          log,
          reservation: r,
          cfg,
          prop,
          mode: "preview",
          cautionDue,
        })
        if (mounted) setHtml(generatedHtml)
      } catch (e) {
        if (mounted) setError(e.message || String(e))
      }
    }
    render(undefined)
    resolveContratCautionDueClient(log).then((cautionDue) => {
      if (mounted && cautionDue !== undefined) render(cautionDue)
    })
    return () => { mounted = false }
  }, [r, log, cfg, prop, nomContrat])

  function handlePreview() {
    setError(null); setInfo(null)
    if (!html) { setError("Contrat non disponible."); return }
    const win = window.open("", "_blank")
    if (!win) { setError("Le popup a été bloqué — autorise les popups pour ce site."); return }
    win.document.write(html); win.document.close()
  }

  async function handleSend() {
    setError(null); setInfo(null)
    if (!nomContrat) { setError(RAISON_CONTRAT_SANS_NOM); return }
    if (!r.guest_email) { setError("Pas d'email voyageur sur cette résa."); return }
    setBusy(true)
    try {
      // CONTRAT V2 (revue M1) — même verdict caution que l'aperçu.
      const cautionDue = await resolveContratCautionDueClient(log)
      const { html: emailHtml } = genContratHTML({
        log,
        reservation: r,
        cfg,
        prop,
        mode: "email",
        cautionDue,
      })
      const subject = `Votre contrat — ${nomContrat}`
      const { data: { session } } = await supabase.auth.getSession()
      const token = session?.access_token
      if (!token) throw new Error("Pas de session admin active")
      const resp = await fetch("/api/send-email", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          type: "contrat_send",
          data: {
            to: r.guest_email,
            subject,
            html: emailHtml,
            logementId: r.logement_id,
            // PR #169b (ADR-106) — discrimine reservationId vs pricingBlockId.
            // FK inbox_messages.pricing_block_id livrée (migration 20260524000001).
            reservationId: itemKind === 'reservation' ? r.id : null,
            pricingBlockId: itemKind === 'pricing_block' ? r.id : null,
          },
        }),
      })
      const j = await resp.json().catch(() => ({}))
      if (!resp.ok) {
        throw new Error(j.error || `HTTP ${resp.status}`)
      }
      // BUG 1 — toast honnête selon le gate dry-run renvoyé par le serveur.
      setInfo(j.dryRun
        ? `Mode test (DRY_RUN) — contrat NON envoyé à ${r.guest_email}.`
        : `Contrat envoyé à ${r.guest_email}`)
    } catch (e) {
      setError(e.message || String(e))
    } finally { setBusy(false) }
  }

  return (
    <div data-testid="resa-modal-contrat-body">
      <div style={{padding:"12px 14px",background:"#eff6ff",color:"#1e40af",borderRadius:8,fontSize:12,marginBottom:14,lineHeight:1.5,border:"1px solid #bfdbfe"}}>
        Contrat pré-rempli avec les clauses du logement{isBlock ? ' (résa direct interne FBZ)' : ''}. Pour modifier les
        clauses : <strong>Logements &gt; {log?.nom || r.logement_id} &gt; Onglet Contrat</strong>.
      </div>
      {/* PR #169b (ADR-106) — bandeau "Trace audit incomplète" retiré.
          inbox_messages.pricing_block_id FK livrée (migration 20260524000001).
          handleSend ci-dessus envoie pricingBlockId proprement (commit 5). */}
      <div style={{display:"flex",gap:10,flexWrap:"wrap",marginBottom:14}}>
        <button
          data-testid="contrat-tab-preview-button"
          onClick={handlePreview}
          disabled={!html}
          style={{background:DS.navy,color:"#fff",border:"none",borderRadius:8,padding:"11px 20px",fontSize:13,fontWeight:700,cursor:html?"pointer":"not-allowed",opacity:html?1:0.5,fontFamily:DS.fontBody}}
        >
          Aperçu / Imprimer
        </button>
        <button
          data-testid="contrat-tab-send-button"
          onClick={handleSend}
          disabled={busy || !r.guest_email || !html}
          title={!r.guest_email ? "Pas d'email voyageur" : ""}
          style={{background: (busy || !r.guest_email || !html) ? "#94a3b8" : DS.green,color:"#fff",border:"none",borderRadius:8,padding:"11px 20px",fontSize:13,fontWeight:700,cursor:(busy||!r.guest_email||!html)?"not-allowed":"pointer",fontFamily:DS.fontBody}}
        >
          {busy ? "Envoi…" : "Envoyer au voyageur"}
        </button>
      </div>
      {error && <div style={{padding:"10px 14px",background:"#fce8e8",color:DS.red,borderRadius:8,fontSize:12,marginBottom:10}}>{error}</div>}
      {info && <div style={{padding:"10px 14px",background:"#e8f5e9",color:DS.green,borderRadius:8,fontSize:12,marginBottom:10}}>{info}</div>}
      {html && (
        <iframe
          data-testid="contrat-tab-preview-iframe"
          srcDoc={html}
          title="Aperçu contrat"
          style={{width:"100%",height:600,border:"1px solid #e8edf4",borderRadius:8,background:"#fff"}}
        />
      )}
    </div>
  )
}

// ── FactureMiniTab — Phase 2.E.11 + PR #169a-bis (ADR-105) ─────────────
// Onglet Facture inline dans la fiche résa : génère la facture pour
// CETTE résa (pas une liste comme dans LogementCard).
//
// PR #169a-bis : kind-aware. Pour kind='pricing_block' :
//   - si !total_amount OR <=0 → EmptyTabPlaceholder ("Pas de facture")
//   - sinon : facture générée avec source='direct' (via mapper, Q-D10),
//     lignes 0 (cleaning_fee/city_tax/ota_commission) masquées via
//     `Number(null) || 0` côté facture-resa-html (MPR169a-bis-1).
// PR #169a-bis Commit 5 — exporté pour tests unitaires (Q-D11 Option 2).
export function FactureMiniTab({ r, itemKind, logementInfo, cfg }) {
  const [info, setInfo] = useState(null)
  const [error, setError] = useState(null)
  // Chantier C (calque FactureTab) : séquences pré-chargées au mount (lecture
  // seule) pour afficher le PROCHAIN numéro PRÉVU dans l'aperçu voyageur, sans
  // JAMAIS le consommer. Émission réelle = onglet Facture du logement.
  const [sequences, setSequences] = useState(null)

  // Architecture multi-émetteur : sous-loc p6 → FBZ (série V), sinon proprio.
  // resolveInvoicePrefix throw si proprio sans invoice_prefix → on capture pour
  // gater le pré-chargement des séquences (l'aperçu retombe alors sur le fallback).
  const prop = logementInfo?.prop || null
  const emitter = resolveEmitter({ cfg, prop })
  // Libellé du document selon l'émetteur (proprio → « Reçu », sous-loc p6 →
  // « Facture ») — source unique, pas de string 'p6' copiée. Aperçu seul ici.
  const tabDocLabel = docLabelForProp(prop)
  const tabDocLabelLow = docLabelForProp(prop, { lower: true })
  let invoicePrefix = null
  let prefixError = null
  try {
    invoicePrefix = resolveInvoicePrefix({ emitter, prop, docType: 'voyageur' })
  } catch (e) {
    prefixError = e.message || 'préfixe non résolu'
  }

  // Pré-chargement des séquences (lecture seule, getInvoiceSequences) AU MOUNT,
  // gardé par !prefixError (proprio sans préfixe → aperçu sans numéro trompeur).
  // Placé AVANT le early-return isBlock (Rules of Hooks). Échec silencieux :
  // l'aperçu retombe sur le fallback (popup s'ouvre quand même).
  useEffect(() => {
    if (prefixError) return
    let mounted = true
    ;(async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession()
        if (!session?.access_token) return
        const resp = await fetch("/api/beds24-mapping?action=getInvoiceSequences", {
          method: "POST",
          headers: { "Content-Type": "application/json", "Authorization": `Bearer ${session.access_token}` },
          body: JSON.stringify({ action: "getInvoiceSequences" }),
        })
        const j = await resp.json().catch(() => ({}))
        if (resp.ok && mounted) setSequences(j.sequences || [])
      } catch (e) {
        if (mounted) console.warn('[FactureMiniTab] getInvoiceSequences:', e?.message)
      }
    })()
    return () => { mounted = false }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // On reconstitue un log à partir de cfg.props[].logs[] OU chambres sous-loc.
  // PR #168 D-1 — utilise findLogementInCfg pour couvrir les chambres feuilles
  // (lc1-lc3 sous-loc p6 "Maison Zen") qui ne sont PAS exposées au niveau
  // cfg.props[].logs directement.
  //
  // BUG 14 — RÉFÉRENCE VIVANTE : cet aperçu lit le nom logement/proprio À JOUR
  // (resolveEmitter(prop) + log.nom_complet/nom résolus depuis le cfg COURANT,
  // re-mémoïsé par ResaV2Modal sur [item, cfg]). Un renommage proprio/logement se
  // reflète donc immédiatement dans l'aperçu. La copie FIGÉE n'intervient qu'à
  // l'ÉMISSION réelle (onglet Facture du logement → html_snapshot gelé) — JAMAIS
  // ici (aperçu seul, aucune écriture DB).
  const log = (() => {
    if (!cfg) return { id: r.logement_id, nom: logementInfo?.nom || r.logement_id, nom_complet: logementInfo?.nom_complet }
    const found = findLogementInCfg(cfg, r.logement_id)
    if (found) return found
    return { id: r.logement_id, nom: logementInfo?.nom || r.logement_id }
  })()

  // PR #169a-bis — pricing_block sans total_amount = pas de facture possible.
  const isBlock = itemKind === 'pricing_block'
  if (isBlock && (r.total_amount == null || Number(r.total_amount) <= 0)) {
    return (
      <EmptyTabPlaceholder
        icon={Receipt}
        title={`Pas de ${tabDocLabelLow}`}
        message={`Renseigner le voyageur + total_amount (> 0) via l'onglet Infos pour activer le ${tabDocLabelLow}.`}
      />
    )
  }

  // APERÇU SEUL (décision Dimitri, CGI 289) — aucune émission ici. L'émission
  // réelle voyageur (numéro séquentiel consommé en DB) = onglet Facture du
  // logement. SYNCHRONE : window.open jamais bloqué par le popup-blocker.
  function handlePreview() {
    setError(null); setInfo(null)
    // Numéro PRÉVU (lecture seule) depuis les séquences en state (pré-chargées au
    // mount). Si série absente / non chargée → null → le générateur retombe sur
    // le fallback `{prefix}-{UUID8}`, suffixé « (aperçu) ». JAMAIS consommé.
    const previewNumber = computePreviewNumber({
      prefix: invoicePrefix,
      year: new Date().getFullYear(),
      sequences,
    })
    const { html } = genFactureResaHTML({
      reservation: r, log, prop, cfg, mode: "preview",
      prefix: invoicePrefix, previewNumber,
    })
    // Faille N1 : le document s'ouvre dans un cadre isolé, jamais écrit
    // dans une page de l'appli (src/lib/apercu-isole.js).
    if (!ouvrirApercuIsole(html)) { setError("Le popup a été bloqué — autorise les popups pour ce site."); return }
  }

  return (
    <div>
      <div style={{padding:"12px 14px",background:"#E3F2FD",color:"#0d47a1",borderRadius:8,fontSize:12,marginBottom:14,lineHeight:1.5,border:"1px solid #90caf9"}}>
        {tabDocLabel} pour cette résa : <strong>{[r.guest_first_name, r.guest_last_name].filter(Boolean).join(" ") || "voyageur"}</strong>{r.guest_email ? ` · ${r.guest_email}` : ' (pas d\'email)'} · {r.start_date} → {r.end_date}
        <div style={{marginTop:6,fontWeight:400}}>Aperçu seul — émission réelle : onglet Reçu voyageur du logement.</div>
      </div>
      <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
        <button onClick={handlePreview}
          style={{background:DS.navy,color:"#fff",border:"none",borderRadius:8,padding:"11px 20px",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:DS.fontBody}}>
          Aperçu / Imprimer
        </button>
      </div>
      {error && <div style={{marginTop:12,padding:"10px 14px",background:"#fce8e8",color:DS.red,borderRadius:8,fontSize:12}}>{error}</div>}
      {info && <div style={{marginTop:12,padding:"10px 14px",background:"#e8f5e9",color:DS.green,borderRadius:8,fontSize:12}}>{info}</div>}
      <div style={{marginTop:14,fontSize:11,color:"#aaa",lineHeight:1.5,padding:"10px 12px",background:"#f8fafd",borderRadius:8}}>
        Pour la liste de tous les documents voyageur du logement, voir <strong>Logements &gt; Documents &gt; Reçu voyageur</strong>. La facture mensuelle proprio reste séparée (Compta).
      </div>
    </div>
  )
}

// ── MenageTab — Phase 4 PR 3 (ADR-076) ──────────────────────────────
// Affiche le menage_run associé à la résa (1:1 via UNIQUE INDEX
// reservation_id). Permet à l'admin :
//   - de voir le statut + provider assigné
//   - de réassigner le provider (override niveau 2)
//   - de marquer le run skipped (ménage non-fait)
//   - de re-scheduler (status='scheduled' depuis skipped)
//
// Si pas de run encore créé : info "sera créé J-1 par le cron 18h-19h".
// L'admin peut aussi créer manuellement le run si urgence.

const STATUS_LABELS_FR = {
  scheduled: 'Prévu',
  in_progress: 'En cours',
  completed: 'Terminé',
  skipped: 'Skip / non-fait',
}
const STATUS_COLORS = {
  scheduled: DS.navy,
  in_progress: DS.amber,
  completed: DS.green,
  skipped: '#94a3b8',
}

function MenageTab({ r, logementInfo }) {
  const [run, setRun] = useState(null)
  const [runLoading, setRunLoading] = useState(true)
  const [runError, setRunError] = useState(null)
  const [users, setUsers] = useState([])
  const [busy, setBusy] = useState(false)
  const [info, setInfo] = useState(null)
  // LOT Photos ménage 27 — restitution admin LECTURE SEULE :
  //   - logInfo : config logement (pieces + items) pour résoudre les labels
  //   - incidents : incidents rattachés à ce run (RLS admin_all)
  const [logInfo, setLogInfo] = useState(null)
  const [runIncidents, setRunIncidents] = useState([])
  // ADR-208 — édition admin de la checklist. currentUser admin reconstruit depuis
  // la session (pas de threading) ; le bouton n'apparaît que si role==='admin'.
  // La RLS admin_all reste l'enforcement réel côté serveur.
  const [adminUser, setAdminUser] = useState(null)
  const [editorOpen, setEditorOpen] = useState(false)

  const reload = async () => {
    setRunLoading(true); setRunError(null)
    try {
      const { data, error } = await supabase
        .from('menage_runs')
        .select('id, logement_id, scheduled_date, assigned_provider_email, status, completed_at, completed_by_email, corrected_by, corrected_at, items_checked, photos, notes, tarif_eur, created_at, updated_at')
        .eq('reservation_id', r.id)
        .maybeSingle()
      if (error) throw error
      setRun(data || null)
    } catch (e) { setRunError(e.message || String(e)) }
    finally { setRunLoading(false) }
  }

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession()
        if (!session?.access_token) return
        // ADR-208 — currentUser admin pour l'édition checklist. Rôle lu de
        // app_metadata.role UNIQUEMENT (= source canonique de la RLS
        // current_role_safe() ; pas user_metadata, falsifiable). role!=='admin'
        // → pas de bouton (et la RLS admin_all reste l'arbitre serveur).
        const u = session.user
        const role = u?.app_metadata?.role || null
        if (!cancelled && u && role === 'admin') setAdminUser({ email: u.email, role })
        const res = await fetch('/api/admin-users', {
          headers: { Authorization: `Bearer ${session.access_token}` },
        })
        if (!res.ok) return
        const { users: list } = await res.json()
        if (!cancelled) setUsers(Array.isArray(list) ? list : [])
      } catch (_) { /* silent — fallback dropdown vide */ }
    })()
    return () => { cancelled = true }
  }, [])

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { reload() }, [r.id])

  // LOT Photos ménage 27 — charge la config logement (pieces/items) pour
  // résoudre item.id -> label dans la restitution items_checked. Best-effort :
  // en cas d'échec, le rendu retombe sur le compteur brut (fallback id).
  useEffect(() => {
    let cancelled = false
    if (!run?.logement_id) { setLogInfo(null); return }
    ;(async () => {
      try {
        const data = await fetchLogInfo(run.logement_id, run.scheduled_date)
        if (!cancelled) setLogInfo(data)
      } catch (_) { if (!cancelled) setLogInfo(null) }
    })()
    return () => { cancelled = true }
  }, [run?.logement_id, run?.scheduled_date])

  // LOT Photos ménage 27 — incidents rattachés à ce run (lecture admin RLS).
  useEffect(() => {
    let cancelled = false
    if (!run?.id) { setRunIncidents([]); return }
    ;(async () => {
      try {
        const list = await listIncidentsByRun(run.id)
        if (!cancelled) setRunIncidents(Array.isArray(list) ? list : [])
      } catch (e) {
        console.error('[MenageTab] listIncidentsByRun', e?.message || e)
        if (!cancelled) setRunIncidents([])
      }
    })()
    return () => { cancelled = true }
  }, [run?.id])

  const candidates = users.filter(u => Array.isArray(u.logIds) && u.logIds.includes(r.logement_id))
  const orphan = run?.assigned_provider_email && !candidates.some(c => c.email === run.assigned_provider_email)

  async function patch(patchObj, infoMsg) {
    setBusy(true); setInfo(null); setRunError(null)
    try {
      const { error } = await supabase.from('menage_runs').update(patchObj).eq('id', run.id)
      if (error) throw error
      if (infoMsg) setInfo(infoMsg)
      await reload()
    } catch (e) { setRunError(e.message || String(e)) }
    finally { setBusy(false) }
  }

  async function createManual() {
    setBusy(true); setInfo(null); setRunError(null)
    try {
      const insertPayload = {
        logement_id: r.logement_id,
        reservation_id: r.id,
        scheduled_date: r.end_date,
        assigned_provider_email: null, // admin assignera ensuite
        status: 'scheduled',
      }
      const { error } = await supabase.from('menage_runs').insert(insertPayload)
      if (error) throw error
      setInfo('Run créé manuellement. Assigne un prestataire ci-dessous.')
      await reload()
    } catch (e) { setRunError(e.message || String(e)) }
    finally { setBusy(false) }
  }

  if (runLoading) return <div style={{padding:20,fontSize:13,color:"#888"}}>Chargement…</div>

  if (!run) {
    return (
      <div style={{padding:"16px 20px",fontSize:13,color:"#444",lineHeight:1.6}}>
        <p style={{marginBottom:14}}>
          Aucun run de ménage n'est encore créé pour cette résa (checkout {formatDateFR(r.end_date)}).
        </p>
        <p style={{marginBottom:14,fontSize:12,color:"#666",padding:"10px 12px",background:"#eef2f7",borderRadius:8}}>
          Le run sera créé automatiquement la veille du checkout par le cron <code>menage-runs</code> (18h-19h Paris). Tu peux aussi le créer manuellement maintenant si besoin :
        </p>
        <button onClick={createManual} disabled={busy}
          style={{background: busy ? '#94a3b8' : DS.navy,color:'#fff',border:'none',borderRadius:8,padding:'10px 18px',fontSize:13,fontWeight:700,cursor:busy?'not-allowed':'pointer',fontFamily:DS.fontBody}}>
          {busy ? 'Création…' : 'Créer le run maintenant'}
        </button>
        {runError && <div style={{marginTop:12,padding:"10px 14px",background:"#fce8e8",color:DS.red,borderRadius:8,fontSize:12}}>{runError}</div>}
        {info && <div style={{marginTop:12,padding:"10px 14px",background:"#e8f5e9",color:DS.green,borderRadius:8,fontSize:12}}>{info}</div>}
      </div>
    )
  }

  return (
    <div style={{padding:"16px 20px",fontSize:13,lineHeight:1.6}}>
      {/* Statut + scheduled_date + tarif */}
      <div style={{display:"flex",alignItems:"center",gap:10,flexWrap:"wrap",marginBottom:14,padding:"12px 14px",background:"#f8fafd",borderRadius:8}}>
        <span style={{
          background: STATUS_COLORS[run.status],
          color:'#fff', padding:'5px 10px', borderRadius:6, fontSize:11, fontWeight:700,
        }}>
          {STATUS_LABELS_FR[run.status] || run.status}
        </span>
        <span style={{fontSize:12,color:"#555"}}>
          Prévu : <strong>{formatDateFR(run.scheduled_date)}</strong>
        </span>
        {run.tarif_eur != null && (
          <span style={{fontSize:12,color:"#555"}}>
            Tarif : <strong>{fmtE(run.tarif_eur)}</strong>
          </span>
        )}
      </div>

      {/* Provider assigné — dropdown réassignation (admin override niveau 2) */}
      <div style={{marginBottom:16}}>
        <label style={{display:"block",fontSize:11,fontWeight:700,color:"#555",marginBottom:5,textTransform:"uppercase",letterSpacing:".4px"}}>
          Prestataire assigné (override par résa)
        </label>
        <select
          value={run.assigned_provider_email || ''}
          onChange={e => patch({ assigned_provider_email: e.target.value || null }, 'Provider mis à jour')}
          disabled={busy}
          style={{width:"100%",padding:"10px 12px",border:"1px solid #dde3ec",borderRadius:8,fontSize:13,background:"#fff",fontFamily:DS.fontBody,cursor: busy ? 'wait' : 'pointer'}}
        >
          <option value="">— Non assigné (admin doit assigner) —</option>
          {orphan && (
            <option value={run.assigned_provider_email}>
              {run.assigned_provider_email} (visibilité retirée)
            </option>
          )}
          {candidates.map(c => (
            <option key={c.id || c.email} value={c.email}>{c.email}</option>
          ))}
        </select>
        {orphan && (
          <div style={{fontSize:11,color:DS.amber,marginTop:6,padding:"8px 10px",background:"#fff7e6",border:`1px solid ${DS.amber}55`,borderRadius:6,lineHeight:1.5}}>
            Le compte <strong>{run.assigned_provider_email}</strong> n'a plus accès à ce logement. Le ménage actuel ne pourra pas voir ce run dans son app. Réassigne ci-dessus.
          </div>
        )}
        {!run.assigned_provider_email && candidates.length === 0 && (
          <div style={{fontSize:11,color:"#666",marginTop:6,padding:"8px 10px",background:"#eef2f7",borderRadius:6,lineHeight:1.5}}>
            Aucun compte ménage n'a accès à ce logement. Configure d'abord l'attribution dans <strong>Paramètres &gt; Comptes</strong>.
          </div>
        )}
      </div>

      {/* Exécution si completed/in_progress */}
      {(run.status === 'completed' || run.status === 'in_progress') && (
        <div style={{marginBottom:16,padding:"12px 14px",background:"#e8f5e9",borderRadius:8,fontSize:12,lineHeight:1.5}}>
          {run.completed_at && (
            <div>Terminé par <strong>{run.completed_by_email || '?'}</strong> le {formatDateTimeFR(run.completed_at)}</div>
          )}
          {/* ADR-208 — trace de correction admin (auteur d'origine préservé ci-dessus). */}
          {run.corrected_at && (
            <div style={{marginTop:4}}>Corrigé par <strong>{run.corrected_by || '?'}</strong> le {formatDateTimeFR(run.corrected_at)}</div>
          )}
          {run.notes && <div style={{marginTop:6,fontStyle:"italic",color:"#666"}}>"{run.notes}"</div>}
        </div>
      )}

      {/* LOT Photos ménage 27 — restitution admin LECTURE SEULE :
          (a) galerie photos groupées par pièce + (b) items cochés avec labels. */}
      <MenageRestitution run={run} pieces={logInfo?.menage?.pieces || []} />

      {/* (c) Incidents signalés sur ce ménage (LECTURE SEULE) */}
      <RunIncidentsSection incidents={runIncidents} />

      {/* Actions admin */}
      <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
        {/* ADR-208 — édition admin de la checklist (exempté du verrou 48h via
            admin_all). MASQUÉ pour les runs FUTURS (scheduled_date > aujourd'hui) :
            on garde le verrou anti-anticipation pour tous. Usage = rattraper le
            passé + corriger. */}
        {adminUser && run.scheduled_date <= todayParisIso() && (
          <button onClick={() => setEditorOpen(true)} disabled={busy}
            style={{background: busy ? '#94a3b8' : DS.green,color:'#fff',border:'none',borderRadius:7,padding:'8px 14px',fontSize:12,fontWeight:700,cursor:busy?'not-allowed':'pointer',fontFamily:DS.fontBody}}>
            {run.status === 'completed' ? 'Corriger' : 'Remplir'} la checklist
          </button>
        )}
        {run.status === 'scheduled' && (
          <button onClick={() => patch({ status: 'skipped' }, 'Marqué skipped')} disabled={busy}
            style={{background: busy ? '#94a3b8' : '#94a3b8',color:'#fff',border:'none',borderRadius:7,padding:'8px 14px',fontSize:12,fontWeight:600,cursor:busy?'not-allowed':'pointer',fontFamily:DS.fontBody}}>
            Marquer skipped (non-fait)
          </button>
        )}
        {run.status === 'skipped' && (
          <button onClick={() => patch({ status: 'scheduled' }, 'Re-scheduled')} disabled={busy}
            style={{background: busy ? '#94a3b8' : DS.navy,color:'#fff',border:'none',borderRadius:7,padding:'8px 14px',fontSize:12,fontWeight:600,cursor:busy?'not-allowed':'pointer',fontFamily:DS.fontBody}}>
            Re-scheduler
          </button>
        )}
      </div>

      {runError && <div style={{marginTop:12,padding:"10px 14px",background:"#fce8e8",color:DS.red,borderRadius:8,fontSize:12}}>{runError}</div>}
      {info && <div style={{marginTop:12,padding:"10px 14px",background:"#e8f5e9",color:DS.green,borderRadius:8,fontSize:12}}>{info}</div>}

      {/* ADR-208 — éditeur checklist admin (réutilise ChecklistRunModal en
          contexte admin : pastLocked=false). onClose/onCompleted → reload() pour
          rafraîchir l'aperçu (items_checked sauvegardés en continu par debounce). */}
      {editorOpen && adminUser && (
        <AdminChecklistEditor
          run={{ ...run, logement_id: run.logement_id || r.logement_id }}
          currentUser={adminUser}
          onClose={() => { setEditorOpen(false); reload() }}
          onCompleted={() => { setEditorOpen(false); reload() }}
        />
      )}
    </div>
  )
}

// ── AdminChecklistEditor — ADR-208 (Lot 2 verrou 48h) ────────────────
// Wrapper admin autour de ChecklistRunModal. RAISON D'ÊTRE : monter le drain de
// la file photo (useMenagePhotoUploadQueue) — sinon les photos enqueue par
// ChecklistRunModal ne seraient jamais commitées → « Terminer » resterait bloqué
// sur les logements à photos_min>0 (ex. lc1, somme 6). onUploaded mutualisé avec
// MenageTodayView via commitUploadedPhoto.
// NE JAMAIS monter en même temps que MenageTodayView (double drain concurrent
// sur menage_runs.photos = last-write-wins). En pratique : desktop admin vs
// mobile ménage = contextes disjoints, risque nul.
// LOT « Ménages dans Opérations » : EXPORTÉ pour réutilisation par MenagesOpsTab
// (sous-onglet admin Opérations > Ménages) — même composant = même drain UNIQUE
// admin, jamais dupliqué. Exclusivité garantie : les onglets admin sont rendus
// un seul à la fois (ResaV2Modal vit dans d'autres onglets que « Ménages »), et
// MenageTodayView n'est jamais monté pour le rôle admin.
export function AdminChecklistEditor({ run, currentUser, onClose, onCompleted }) {
  // Lot « travail perdu » : l'erreur d'enregistrement REMONTE à la file
  // (use-photo-upload-queue) — c'est ce qui lui fait garder la photo et la
  // retenter. L'avaler ici faisait croire à un succès : photo retirée, perdue.
  const onUploaded = useCallback(async (item) => {
    await commitUploadedPhoto(supabase, item)
  }, [])
  useMenagePhotoUploadQueue({ onUploaded })
  return (
    <ChecklistRunModal run={run} currentUser={currentUser} onClose={onClose} onCompleted={onCompleted} />
  )
}

// ── MenageRestitution — LOT Photos ménage 27 ─────────────────────────
// Restitution admin LECTURE SEULE de l'exécution d'un menage_run :
//   (a) galerie de vignettes run.photos groupées par piece_id
//   (b) détail items_checked groupé par pièce (item.id -> label via pieces ;
//       fallback compteur brut / id si la pièce/l'item n'existe plus en config)
// Aucune écriture : pas de delete photo, pas d'action. Vignettes en
// <a target="_blank"> comme PieceBlock côté ménage.

function MenageRestitution({ run, pieces }) {
  // Chantier photo (Lot 1) : photos de pièce dans un bucket PRIVÉ → on récupère
  // les signed URL via l'endpoint serveur (admin). Fallback run.photos (legacy
  // public) le temps du fetch. Lecture seule.
  const [signed, setSigned] = useState(null)
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession()
        const token = session?.access_token
        if (!token) return
        const res = await fetch(`/api/admin-users?action=menage_run_photos&run_id=${encodeURIComponent(run.id)}`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        const json = await res.json().catch(() => ({}))
        if (!cancelled && Array.isArray(json.photos)) setSigned(json.photos)
      } catch (_) { /* swallow */ }
    })()
    return () => { cancelled = true }
  }, [run.id])

  const photos = (signed || (Array.isArray(run.photos) ? run.photos : [])).filter((p) => p && (p.url || p.path))
  const photosByPiece = groupPhotosByPiece(photos)
  const itemsDetail = buildItemsCheckedDetail(run.items_checked, pieces)
  const pieceLabel = (pieceId) => {
    const p = (pieces || []).find(x => x && x.id === pieceId)
    return p ? `${p.icon ? p.icon + ' ' : ''}${p.name || pieceId}` : pieceId
  }

  if (photos.length === 0 && itemsDetail.length === 0) {
    return (
      <div style={{marginBottom:16,padding:"12px 14px",background:"#f8fafd",borderRadius:8,fontSize:12,color:"#888"}}>
        Aucune photo ni item coché enregistré pour ce ménage.
      </div>
    )
  }

  return (
    <div style={{marginBottom:16}}>
      {/* (a) Galerie photos groupées par pièce */}
      {photos.length > 0 && (
        <div style={{marginBottom:14}}>
          <div style={{fontSize:11,fontWeight:700,color:"#555",marginBottom:8,textTransform:"uppercase",letterSpacing:".4px"}}>
            Photos ({photos.length})
          </div>
          {Object.keys(photosByPiece).map(pieceId => (
            <div key={pieceId} style={{marginBottom:10}}>
              <div style={{fontSize:12,fontWeight:600,color:DS.navy,marginBottom:5}}>
                {pieceLabel(pieceId)}
              </div>
              <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                {photosByPiece[pieceId].map((p, i) => p.url ? (
                  <a key={p.url || i} href={p.url} target="_blank" rel="noopener noreferrer"
                    title={p.uploaded_at ? formatDateTimeFR(p.uploaded_at) : undefined}
                    style={{display:"block",width:72,height:72,borderRadius:6,overflow:"hidden",border:"1px solid #e8edf4"}}>
                    <img src={p.url} alt={`preuve ménage ${i + 1}`} style={{width:"100%",height:"100%",objectFit:"cover"}}/>
                  </a>
                ) : null)}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* (b) Items cochés groupés par pièce */}
      {itemsDetail.length > 0 && (
        <div>
          <div style={{fontSize:11,fontWeight:700,color:"#555",marginBottom:8,textTransform:"uppercase",letterSpacing:".4px"}}>
            Items cochés
          </div>
          {itemsDetail.map(d => (
            <div key={d.pieceId} style={{marginBottom:8,padding:"8px 10px",background:"#f8fafd",borderRadius:8}}>
              <div style={{fontSize:12,fontWeight:600,color:DS.navy,marginBottom:4}}>
                {d.pieceKnown
                  ? `${d.pieceIcon ? d.pieceIcon + ' ' : ''}${d.pieceName || d.pieceId}`
                  : `${d.pieceId} (pièce retirée de la config)`}
                {' · '}<span style={{color:"#888",fontWeight:400}}>{d.checkedCount} coché{d.checkedCount > 1 ? 's' : ''}</span>
              </div>
              {d.items.length > 0 && (
                <ul style={{margin:0,paddingLeft:18,fontSize:12,color:"#555",lineHeight:1.6}}>
                  {d.items.map((it, i) => (
                    <li key={it.id || i}>{it.label || <span style={{color:"#aaa"}}>{it.id} (item inconnu)</span>}</li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ── RunIncidentsSection — LOT Photos ménage 27 ───────────────────────
// (c) Incidents signalés sur ce ménage (run_id), LECTURE SEULE.
// type/status/photos/notes + pointeur vers l'onglet Incidents pour la
// gestion (transitions). Aucune action de transition ici.

function RunIncidentsSection({ incidents }) {
  // Lot 2 photo (ADR-209) : les photos d'incident sont sur le bucket PRIVÉ →
  // on récupère les signed URL via l'endpoint (legacy public = pass-through).
  // Clé = incident.id. Best-effort : si échec, fallback inc.photos brut.
  const [signedByInc, setSignedByInc] = useState({})
  useEffect(() => {
    let cancelled = false
    const list = Array.isArray(incidents) ? incidents.filter((i) => Array.isArray(i.photos) && i.photos.length) : []
    if (!list.length) { setSignedByInc({}); return }
    ;(async () => {
      const entries = await Promise.all(list.map(async (inc) => [inc.id, await fetchIncidentPhotos(inc.id)]))
      if (!cancelled) setSignedByInc(Object.fromEntries(entries))
    })()
    return () => { cancelled = true }
  }, [incidents])

  if (!Array.isArray(incidents) || incidents.length === 0) return null
  return (
    <div style={{marginBottom:16}}>
      <div style={{fontSize:11,fontWeight:700,color:"#555",marginBottom:8,textTransform:"uppercase",letterSpacing:".4px"}}>
        Incidents signalés sur ce ménage ({incidents.length})
      </div>
      {incidents.map(inc => {
        const meta = statusMeta(inc.status)
        const degs = Array.isArray(inc.degradations) ? inc.degradations : []
        // Photos signées (bucket privé) si dispo, sinon fallback brut (legacy public).
        const photos = signedByInc[inc.id] || (Array.isArray(inc.photos) ? inc.photos : [])
        return (
          <div key={inc.id} style={{marginBottom:8,padding:"10px 12px",background:"#fff",border:"1px solid #f0e0e0",borderRadius:8}}>
            <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap",marginBottom:6}}>
              <span style={{fontSize:11,padding:"3px 8px",borderRadius:5,background:meta.color,color:"#fff",fontWeight:700}}>
                {meta.icon} {meta.label}
              </span>
              {inc.label && <span style={{fontSize:12,fontWeight:600,color:DS.navy}}>{inc.label}</span>}
              <span style={{fontSize:11,color:"#999"}}>
                {inc.incident_date ? formatDateFR(inc.incident_date) : (inc.reported_at ? formatDateFR(inc.reported_at) : '')}
              </span>
            </div>
            {degs.length > 0 && (
              <div style={{fontSize:12,color:"#444",marginBottom:6}}>
                {degs.map((dg, i) => (
                  <span key={dg.id || i} style={{display:"inline-block",marginRight:8}}>
                    <span style={{background:"#fce8e8",color:DS.red,padding:"1px 6px",borderRadius:4,fontWeight:700,fontSize:10}}>{dg.type}</span>
                    {dg.nombre && dg.nombre !== '1' && <strong> ×{dg.nombre}</strong>}
                    {dg.precision && <span style={{fontStyle:"italic",color:"#666"}}> — {dg.precision}</span>}
                  </span>
                ))}
              </div>
            )}
            {inc.description && (
              <div style={{fontSize:12,color:"#666",fontStyle:"italic",marginBottom:6}}>{inc.description}</div>
            )}
            {photos.length > 0 && (
              <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                {photos.map((ph, i) => {
                  const url = typeof ph === 'string' ? ph : (ph && ph.url)
                  if (!url) return null
                  return (
                    <a key={url || i} href={url} target="_blank" rel="noopener noreferrer"
                      style={{display:"block",width:64,height:64,borderRadius:6,overflow:"hidden",border:"1px solid #f0e0e0"}}>
                      <img src={url} alt={`incident ${i + 1}`} style={{width:"100%",height:"100%",objectFit:"cover"}}/>
                    </a>
                  )
                })}
              </div>
            )}
          </div>
        )
      })}
      <div style={{fontSize:11,color:"#888",marginTop:6,lineHeight:1.5}}>
        Pour traiter ces incidents (vérifier / racheter), ouvre l'onglet <strong>Incidents</strong>.
      </div>
    </div>
  )
}

// ── FooterActions — PR #169a-bis (ADR-105) C3 ─────────────────────────
// Pour kind='pricing_block' uniquement. Affiche :
//   - Bouton "Pousser maintenant" si block.beds24_pushed=false ET block
//     simple (sans voyageur) ET active. Les résas directes (avec voyageur)
//     sont poussées Beds24 dès la création (ADR-181) — en cas d'échec, le cron
//     outbox les retente seul, pas de push manuel exposé pour ce cas.
//   - Bouton "Débloquer" si block.active ET !released_at (Q-D10bis).
//   - Bandeau "Période libérée le DD/MM/YYYY" si released_at.
//
// Logique migrée depuis PricingBlockDetailsModal.jsx (handleRelease + handlePushNow).

function FooterActions({ item, dryRunActive, onAction }) {
  const [releasing, setReleasing] = useState(false)
  const [confirmRelease, setConfirmRelease] = useState(false)
  const [pushing, setPushing] = useState(false)
  const [serverError, setServerError] = useState(null)
  const [toast, setToast] = useState(null)
  const [pushDisabledUntil, setPushDisabledUntil] = useState(0)
  const [, forceTick] = useState(0) // tick reactif disable timer

  const block = item.raw
  const blockId = item.id
  // 2026-09-15 : « libérée » = released_at ET plus actif. Un bloc ACTIF avec
  // released_at = libération DEMANDÉE, en attente de confirmation Beds24 (la
  // période reste fermée à la vente) — ni « libérée », ni « à libérer ».
  const isReleased = !!block.released_at && block.active !== true
  const isReleasePending = !!block.released_at && block.active === true
  // ⚠️ 2026-09-17 — CET ÉTAT EST DÉSORMAIS *MACHINE SEULEMENT*, ET LE BOUTON
  // « Réessayer l'envoi » PLUS BAS RESTE DONC UTILE. Le bouton « Débloquer »
  // de cette fiche ne met PLUS rien en file : quand Beds24 ne répond pas, il
  // échoue franchement et ne touche à rien (décision Dimitri, chemin humain =
  // pas de durabilité différée). Ce qui produit encore un bloc ACTIF avec
  // `released_at`, ce sont les chemins AUTOMATIQUES — au premier rang
  // `api/cron/release-expired-bookings.js`, qui pose exactement ce couple
  // (`released_at` sans `active:false`) quand il a réussi à mettre l'item en
  // file. C'est ce que Dimitri voit ici, et c'est ce que le bouton rejoue.
  // Le supprimer laisserait ces libérations automatiques sans issue à l'écran
  // après cinq échecs — la situation même qui a fait naître ce bouton (#1426).
  // 2026-09-17 : `isDirect` (= « un prénom ⇒ une vente ») retiré. Il n'était
  // JAMAIS utilisé, et il affirmait l'inverse de la seule liste qui fait foi
  // (`lib/pricing-block-nature.js` : un prénom seul = un blocage que Dimitri
  // note, donc débloquable — 21 des 34 blocs de prod). Un vestige qui
  // contredit la règle finit par être recopié par quelqu'un.

  // Re-render lorsque le disable timer expire (cosmétique).
  useEffect(() => {
    if (pushDisabledUntil <= Date.now()) return
    const remaining = pushDisabledUntil - Date.now()
    const t = setTimeout(() => forceTick((x) => x + 1), remaining + 50)
    return () => clearTimeout(t)
  }, [pushDisabledUntil])

  const pushDisabled = pushDisabledUntil > Date.now() || pushing || releasing
  const remainingSeconds = Math.max(
    0,
    Math.ceil((pushDisabledUntil - Date.now()) / 1000),
  )

  // ⚠️ NOTRE GRAMMAIRE (Dimitri 2026-08-22) : plus de boîte du navigateur.
  function handleRelease() {
    if (releasing) return
    setConfirmRelease(true)
  }

  async function handleReleaseConfirme() {
    setConfirmRelease(false)
    setReleasing(true)
    setServerError(null)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session?.access_token) {
        setServerError('Non authentifié')
        setReleasing(false)
        return
      }
      const res = await fetch(
        '/api/beds24-mapping?action=pricing_block_release',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session.access_token}`,
          },
          // ⚠️ `confirm: true` DEPUIS LE 2026-09-17 — et il vient d'ICI, du
          // chemin qui n'existe qu'APRÈS la modale de confirmation, jamais du
          // bouton lui-même. Le serveur passe désormais par les trois
          // vérifications d'ADR-256/257 (confirmation, relecture des
          // réservations, contrôle en direct chez Beds24) et refuse sans lui.
          // Dimitri : « un bouton qui contourne trois vérifications, c'est la
          // porte de derrière. »
          body: JSON.stringify({ block_id: blockId, confirm: true }),
        },
      )
      const json = await res.json().catch(() => ({}))
      if (!res.ok) {
        setServerError(json.message || json.error || `http_${res.status}`)
        setReleasing(false)
        return
      }
      onAction && onAction('released', { ok: true, ...json })
    } catch (e) {
      setServerError(e.message || String(e))
    } finally {
      setReleasing(false)
    }
  }

  // 2026-09-15 (revue adverse #1426, MAJEUR 2) : `targetBlock` = drain CIBLÉ
  // sur ce bloc (bandeau « libération demandée ») — ré-arme aussi un item
  // abandonné après 5 refus, c'est la seule issue de cet état dans l'app.
  async function handlePushNow({ targetBlock = false } = {}) {
    if (pushDisabled) return
    setPushing(true)
    setServerError(null)
    setToast(null)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session?.access_token) {
        setServerError('Non authentifié')
        setPushing(false)
        return
      }
      const res = await fetch(
        `/api/beds24-mapping?action=pricing_outbox_drain&logement_id=${encodeURIComponent(block.logement_id)}${targetBlock ? `&block_id=${encodeURIComponent(blockId)}` : ''}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session.access_token}`,
          },
        },
      )
      const json = await res.json().catch(() => ({}))
      if (res.status === 429) {
        const seconds = Number(json.retry_after_seconds) || 30
        setPushDisabledUntil(Date.now() + seconds * 1000)
        setToast(`Trop rapide — réessayez dans ${seconds}s`)
        setPushing(false)
        return
      }
      if (!res.ok) {
        setServerError(json.message || json.error || `http_${res.status}`)
        setPushing(false)
        return
      }
      // Revue adverse #1428 (MAJEUR 2) : « 0 item(s) poussé(s) » ressemblait à un
      // succès. Zéro rejoué = rien n'était en attente : le dire tel quel.
      const nbPousses = json.items_pushed || json.retried || 0
      setToast(
        nbPousses === 0
          ? "Aucun envoi en attente pour ce bloc — rien n'est parti vers Beds24."
          : `${nbPousses} item(s) poussé(s) vers Beds24`,
      )
      setPushDisabledUntil(Date.now() + 60 * 1000)
      onAction && onAction('pushed', { ok: true, ...json })
    } catch (e) {
      setServerError(e.message || String(e))
    } finally {
      setPushing(false)
    }
  }

  // Bandeau released — affiché si déjà libéré (Q-D10bis).
  if (isReleasePending) {
    return (
      <div data-testid="resa-modal-footer-release-pending" style={{ padding: '10px 16px', borderTop: '1px solid #e8edf4', background: '#fff7ed', color: '#9a3412', fontSize: 12, lineHeight: 1.5 }}>
        <strong>Libération demandée le {formatDateFR(block.released_at)}, en attente de confirmation Beds24.</strong> La période reste fermée à la vente chez vous et chez Beds24 jusqu'à cette confirmation. L'envoi est en file de rejeu ; s'il échoue cinq fois, vous recevez une alerte. Si Beds24 refuse, rouvrez la période à la main dans Beds24 puis réessayez ici : la confirmation rendra le bloc inactif.
        {serverError && (
          <div data-testid="resa-modal-release-pending-error" style={{ marginTop: 6, color: DS.red, fontWeight: 600 }}>{serverError}</div>
        )}
        {toast && <div style={{ marginTop: 6, fontWeight: 600 }}>{toast}</div>}
        <div style={{ marginTop: 8, textAlign: 'right' }}>
          <button
            data-testid="resa-modal-release-retry-button"
            type="button"
            onClick={() => handlePushNow({ targetBlock: true })}
            disabled={pushDisabled}
            title={pushDisabled && remainingSeconds > 0 ? `Réessayez dans ${remainingSeconds}s` : "Rejouer maintenant l'envoi de la libération à Beds24 (y compris s'il a été abandonné)"}
            style={{ background: '#2563eb', color: '#fff', border: 'none', borderRadius: 7, padding: '7px 12px', fontSize: 12, fontWeight: 700, cursor: pushDisabled ? 'not-allowed' : 'pointer', opacity: pushDisabled ? 0.5 : 1, fontFamily: DS.fontBody }}
          >
            {pushing ? 'Envoi…' : pushDisabled && remainingSeconds > 0 ? `${remainingSeconds}s` : "Réessayer l'envoi"}
          </button>
        </div>
      </div>
    )
  }
  if (isReleased) {
    return (
      <div
        data-testid="resa-modal-footer-actions"
        style={{
          flexShrink: 0,
          padding: '10px 16px',
          borderTop: '1px solid #e8edf4',
          background: '#ecfdf5',
          color: '#065f46',
          fontSize: 12,
          textAlign: 'center',
        }}
      >
        Période libérée le {formatDateFR(block.released_at)}
        {block.released_by && <> par <strong>{block.released_by}</strong></>}
      </div>
    )
  }

  return (
    <>
    {confirmRelease && (
      <FeuilleConfirmation
        testId="resa-confirm-release"
        titre="Débloquer cette période ?"
        texte="Les dates redeviennent réservables sur toutes les plateformes connectées. Si un voyageur réserve entre-temps, le blocage ne pourra pas être remis à l'identique."
        libelleConfirmer="Débloquer la période"
        libelleRenoncer="Garder le blocage"
        ton="danger"
        occupe={releasing}
        onConfirmer={handleReleaseConfirme}
        onRenoncer={() => setConfirmRelease(false)} />
    )}
    <div
      data-testid="resa-modal-footer-actions"
      style={{
        flexShrink: 0,
        padding: '10px 16px',
        borderTop: '1px solid #e8edf4',
        background: '#f8fafd',
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
      }}
    >
      {serverError && (
        <div style={{ padding: '6px 10px', background: '#fdf2f2', color: DS.red, borderRadius: 6, fontSize: 11 }}>
          {serverError}
        </div>
      )}
      {toast && (
        <div role="alert" style={{ padding: '6px 10px', background: '#eff6ff', color: DS.navy, borderRadius: 6, fontSize: 11 }}>
          {toast}
        </div>
      )}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
        {/* Push Beds24 — tout bloc actif dont Beds24 n'a pas confirmé la fermeture,
            résa directe comprise (revue adverse #1428, MAJEUR 1 : ADR-181 pousse
            numAvail=0 pour les deux ; un direct refusé par Beds24 était muet).
            Drain CIBLÉ sur ce bloc (MAJEUR 2) : rejoue même pendant le délai de
            rejeu et même un item abandonné — un clic explicite = « essaie
            maintenant ». */}
        {!block.beds24_pushed && (
          <button
            data-testid="resa-modal-footer-push-button"
            type="button"
            onClick={() => handlePushNow({ targetBlock: true })}
            disabled={pushDisabled}
            title={
              pushDisabled && remainingSeconds > 0
                ? `Réessayez dans ${remainingSeconds}s`
                : dryRunActive
                  ? "Pousser ce block (DRY_RUN — pas d'appel Beds24 réel)"
                  : 'Pousser ce block vers Beds24 maintenant'
            }
            style={{
              background: '#2563eb',
              color: '#fff',
              border: 'none',
              borderRadius: 7,
              padding: '7px 12px',
              fontSize: 12,
              fontWeight: 700,
              cursor: pushDisabled ? 'not-allowed' : 'pointer',
              opacity: pushDisabled ? 0.5 : 1,
              fontFamily: DS.fontBody,
            }}
          >
            {pushing
              ? 'Envoi…'
              : pushDisabled && remainingSeconds > 0
                ? `${remainingSeconds}s`
                : dryRunActive
                  ? 'Pousser (DRY_RUN)'
                  : 'Pousser maintenant'}
          </button>
        )}
        {/* Q-D10bis — bouton Débloquer masqué si released_at NOT NULL (déjà géré
            par early return isReleased ci-dessus). Affiché seulement si block.active. */}
        {block.active && (
          <button
            data-testid="resa-modal-footer-release-button"
            type="button"
            onClick={handleRelease}
            disabled={releasing || pushing}
            style={{
              background: DS.red,
              color: '#fff',
              border: 'none',
              borderRadius: 7,
              padding: '7px 12px',
              fontSize: 12,
              fontWeight: 700,
              cursor: releasing || pushing ? 'not-allowed' : 'pointer',
              opacity: releasing || pushing ? 0.5 : 1,
              fontFamily: DS.fontBody,
            }}
          >
            {releasing ? 'Envoi…' : dryRunActive ? 'Débloquer (dry_run)' : 'Débloquer'}
          </button>
        )}
      </div>
    </div>
    </>
  )
}
